import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { VersionsEnvoyees } from '@/components/crm/VersionsEnvoyees'; // 📦 Phase 6
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DollarSign, Plus, CreditCard, Printer, Send, Trash2,
  CheckCircle, Edit, RotateCcw, X, FileText, PenLine, Package, Wrench, Banknote, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { MoneyInput } from '@/lib/MoneyInput';
import { useEmailPreview, EmailPreviewFrame } from '../EmailPreview';
import { SectionProduits } from './SectionProduits';
import { useAuth } from '@/lib/AuthContext';
import EditeurFacture from './EditeurFacture';
import { API_URL } from '@/lib/apiUrl';
import { urlFichier } from '@/lib/fichiers';
import { BadgeSuiviClient, BlocSuiviClient } from '@/components/crm/SuiviClient'; // 📬 Suivi client

const fmt = (n) => Number(n || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCurrency = (n) => `${fmt(n)} $`;

// ─── Bouton Avis Google avec confirmation ───────────────────────────────────
function AvisGoogleButton({ dossier }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);

  const handleEnvoyer = async () => {
    setSending(true);
    try {
      const clientId = dossier?.client_id || dossier?.id;
      const dossierId = dossier?.id;
      const url = `${API_URL}/api/sms/avis-google/${clientId}${dossierId ? `?dossier_id=${dossierId}` : ''}`;
      const { data: d } = await axios.post(url, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('SMS "Avis 5 étoiles" envoyé au client !');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
      setShowConfirm(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        data-testid="avis-google-facture-btn"
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold"
        onClick={() => setShowConfirm(true)}
      >
        ⭐ Demander Avis 5 Étoiles
      </Button>
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmer l'envoi</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 mt-2">
            Êtes-vous sûr de vouloir envoyer une demande d'avis 5 étoiles par SMS à ce client ?
          </p>
          <div className="flex gap-3 mt-4 justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowConfirm(false)}>Annuler</Button>
            <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-white" onClick={handleEnvoyer} disabled={sending}>
              {sending ? 'Envoi...' : 'Oui, envoyer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const STATUT_COLORS = {
  impaye: 'bg-red-100 text-red-800',
  partiel: 'bg-yellow-100 text-yellow-800',
  paye: 'bg-green-100 text-green-800',
};
const STATUT_LABELS = { impaye: 'Non payé', partiel: 'Partiel', paye: 'Payé' };


// 🆕 [Phase 92 — 2026-07-08] Dialog Rappel Paiement par SMS
// L'utilisatrice édite le template pré-rempli puis envoie manuellement.
function RappelPaiementSmsDialog({ isOpen, onClose, facture, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [texte, setTexte] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setTexte('');
    setPreview(null);
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/sms/preview-rappel-paiement/${facture.id}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
        );
        setPreview(data);
        setTexte(data?.body || '');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Impossible de charger le modèle SMS');
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, facture.id]);

  const handleEnvoyer = async () => {
    if (!texte.trim()) { toast.error('Le message ne peut pas être vide'); return; }
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/sms/rappel-paiement-facture/${facture.id}`,
        { message_custom: texte.trim() !== (preview?.body || '').trim() ? texte : null },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      toast.success('📱 Rappel SMS envoyé au client !');
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi SMS');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dialog-rappel-paiement-sms">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>📱</span>Envoyer rappel de paiement (SMS)
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-gray-500">Chargement du modèle…</p>
        ) : (
          <div className="space-y-3">
            <div className="text-xs bg-blue-50 rounded p-2 space-y-0.5">
              <div><span className="text-gray-600">Client :</span> <span className="font-semibold">{preview?.client_nom || '—'}</span></div>
              <div><span className="text-gray-600">Téléphone :</span> <span className="font-mono">{preview?.phone || '—'}</span></div>
              <div><span className="text-gray-600">Balance à recevoir :</span> <span className="font-bold text-red-700">{fmtCurrency(preview?.solde_du)}</span></div>
            </div>
            <div>
              <Label className="text-xs">Message SMS (modifiable — modèle par défaut chargé depuis Centre Notifications) :</Label>
              <Textarea
                value={texte}
                onChange={(e) => setTexte(e.target.value)}
                rows={9}
                className="mt-1 text-sm font-mono"
                data-testid="textarea-rappel-paiement-sms"
              />
              <p className="mt-1 text-xs text-gray-500">
                {texte.length} caractère(s) — 1 SMS = 160 max, au-delà = plusieurs SMS
              </p>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="btn-rappel-paiement-annuler">
            Annuler
          </Button>
          <Button
            className="bg-orange-600 hover:bg-orange-700"
            onClick={handleEnvoyer}
            disabled={loading || sending || !texte.trim()}
            data-testid="btn-rappel-paiement-envoyer"
          >
            {sending ? 'Envoi…' : '📱 Envoyer maintenant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Génération HTML complète de la facture (ouvre dans nouvelle fenêtre) ───────
function generateFactureHTML(facture, dossier, client, infosEntreprise, ajouts = [], allFactures = []) {
  if (!facture) return '';

  // 🆕 2026-05-15 — Support du type 'complementaire'. Utilise EXACTEMENT le
  // même template que 'finale' (zéro recréation), juste le titre change.
  const titreFacture =
    facture.type_facture === 'depot' ? 'FACTURE DE DÉPÔT' :
    facture.type_facture === 'complementaire' ? `FACTURE COMPLÉMENTAIRE${facture.numero_complementaire ? ' #' + facture.numero_complementaire : ''}` :
    'FACTURE FINALE';
  const isFinaleOrComp = facture.type_facture === 'finale' || facture.type_facture === 'complementaire';
  const factureDepot = allFactures.find(f => f.type_facture === 'depot');
  const montantDepotDemande = factureDepot?.montant_total || 0;
  const montantDepotPaye = facture.type_facture === 'finale'
    ? (factureDepot?.montant_paye_reel || factureDepot?.montant_paye || 0)
    : 0;
  const depotEstPaye = factureDepot && (factureDepot.statut === 'paye' || montantDepotPaye > 0);
  const datePaiementDepot = factureDepot?.date_paiement_reel || factureDepot?.date_paiement || null;
  const totalAjouts = ajouts.reduce((s, a) => s + (a.montant_total || 0), 0);
  // 🆕 [v8] Rabais commercial (montant + description) — affiché seulement si > 0
  const rabaisMontant = parseFloat(facture.rabais_montant) || 0;
  const rabaisDescription = (facture.rabais_description || '').trim();
  const hasRabais = rabaisMontant > 0;
  // 🆕 [v8] Sections : séparer items équipement (location) vs services (peinture)
  const itemsAll = facture.items || [];
  const itemsLocation = itemsAll.filter(it => (it.type || '').toLowerCase() === 'equipement');
  const itemsPeinture = itemsAll.filter(it => (it.type || '').toLowerCase() !== 'equipement');
  // Pour les dossiers cash: solde basé sur montant avant taxes
  const montantFactureSolde = dossier?.paiement_en_argent
    ? (facture.montant_avant_taxes || facture.montant_total)
    : facture.montant_total;
  // Solde dû : on soustrait toujours le dépôt demandé (même si pas encore marqué payé),
  // car le client a normalement déjà envoyé son dépôt quand on produit la facture finale.
  const montantDepotASoustraire = montantDepotPaye > 0 ? montantDepotPaye : montantDepotDemande;
  // 🆕 [Phase v13.42 — 2026-07-14] Facture complémentaire : inclut aussi les ajouts (pas de dépôt).
  const soldeFinalDu = facture.type_facture === 'finale'
    ? Math.max(0, montantFactureSolde + totalAjouts - montantDepotASoustraire)
    : (facture.type_facture === 'complementaire'
        ? Math.max(0, montantFactureSolde + totalAjouts)
        : null);

  // Adresse facturation
  const adresseFact = facture.adresse_facturation || client?.adresse_client || '';
  const villeFact = facture.ville_facturation || client?.ville_client || '';
  const cpFact = facture.code_postal_facturation || client?.code_postal_client || '';

  // Logo URL absolue
  const logoUrl = infosEntreprise?.logo_principal
    ? `${API_URL}${infosEntreprise.logo_principal}`
    : null;

  // 🆕 [v8] Bloc Peinture / Services
  const sectionPeinture = itemsPeinture.length > 0 ? `
    <div style="border:1px solid #c7d2fe; border-radius:8px; overflow:hidden; margin-bottom:12px;">
      <div style="background:#eef2ff; padding:8px 14px; border-bottom:1px solid #c7d2fe; font-weight:700; font-size:12px; color:#3730a3; letter-spacing:0.05em; text-transform:uppercase;">Travaux de peinture</div>
      <table style="width:100%; border-collapse:collapse;">
        <thead><tr style="border-bottom:2px solid #111827;"><th style="text-align:left; padding:8px 12px; font-size:11px; font-weight:700;">Description</th><th style="text-align:right; padding:8px 12px; font-size:11px; font-weight:700; width:140px;">Montant</th></tr></thead>
        <tbody>${itemsPeinture.map(item => `
          <tr>
            <td style="padding:8px 12px; border-bottom:1px solid #e5e7eb; white-space:pre-wrap;">${item.description || ''}</td>
            <td style="padding:8px 12px; border-bottom:1px solid #e5e7eb; text-align:right; font-weight:600;">${fmtCurrency(item.montant)}</td>
          </tr>`).join('')}</tbody>
      </table>
    </div>` : '';

  // 🆕 [v8] Bloc Location d'équipement (avec sous-lignes location/livraison/extras)
  const sectionLocation = itemsLocation.length > 0 ? `
    <div style="border:1px solid #a5f3fc; border-radius:8px; overflow:hidden; margin-bottom:12px;">
      <div style="background:#ecfeff; padding:8px 14px; border-bottom:1px solid #a5f3fc; font-weight:700; font-size:12px; color:#155e75; letter-spacing:0.05em; text-transform:uppercase;">Location d'équipement</div>
      <table style="width:100%; border-collapse:collapse;">
        <thead><tr style="border-bottom:2px solid #111827;"><th style="text-align:left; padding:8px 12px; font-size:11px; font-weight:700;">Description</th><th style="text-align:right; padding:8px 12px; font-size:11px; font-weight:700; width:140px;">Montant</th></tr></thead>
        <tbody>${itemsLocation.map(item => {
          const prixLoc = parseFloat(item.prix_location) || 0;
          const qte = parseFloat(item.quantite) || 1;
          const prixLivr = parseFloat(item.prix_livraison) || 0;
          const extrasList = Array.isArray(item.extras) ? item.extras : [];
          const subLignes = [];
          if (prixLoc > 0) subLignes.push(`<div style="display:flex; justify-content:space-between;"><span>· Location (${item.duree_type || 'jour'}${qte > 1 ? ` × ${qte}` : ''})</span><span>${fmtCurrency(prixLoc * qte)}</span></div>`);
          if (prixLivr > 0) subLignes.push(`<div style="display:flex; justify-content:space-between;"><span>· Livraison</span><span>${fmtCurrency(prixLivr)}</span></div>`);
          extrasList.forEach(ex => {
            subLignes.push(`<div style="display:flex; justify-content:space-between;"><span>· ${ex.description || 'Extra'}</span><span>${fmtCurrency(ex.montant)}</span></div>`);
          });
          const periode = (item.date_prise || item.date_remise)
            ? `<div style="font-size:10px; color:#6b7280; margin-top:2px;">Période : ${item.date_prise || '—'} → ${item.date_remise || '—'} · Qté : ${qte}</div>`
            : '';
          return `
          <tr>
            <td style="padding:8px 12px; border-bottom:1px solid #e5e7eb;">
              <div style="font-weight:600;">${item.description || '—'}</div>
              ${periode}
              ${subLignes.length > 0 ? `<div style="margin-top:6px; padding-left:10px; border-left:2px solid #e5e7eb; font-size:11px; color:#374151; line-height:1.7;">${subLignes.join('')}</div>` : ''}
            </td>
            <td style="padding:8px 12px; border-bottom:1px solid #e5e7eb; text-align:right; font-weight:700; vertical-align:top;">${fmtCurrency(item.montant)}</td>
          </tr>`;
        }).join('')}</tbody>
      </table>
    </div>` : '';

  // ✅ Section dépôt PRQC (département Peinture Résidentielle QC) — s'affiche sur la FACTURE FINALE
  // même quand le dépôt n'est pas encore marqué "payé" dans le système. Visibilité prioritaire.
  const sectionDepotPRQC = (facture.type_facture === 'finale' && factureDepot) ? `
    <div style="border:2px solid #1d4ed8; border-radius:8px; padding:16px 18px; background:#eff6ff; margin-bottom:16px;">
      <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:10px;">
        <h3 style="font-size:14px; font-weight:700; color:#1e3a8a;">💰 Dépôt — Peinture Résidentielle QC</h3>
        <span style="font-size:10px; font-weight:700; padding:3px 8px; border-radius:9999px; ${depotEstPaye ? 'background:#16a34a; color:white;' : 'background:#f59e0b; color:white;'}">
          ${depotEstPaye ? '✓ PAYÉ' : 'EN ATTENTE'}
        </span>
      </div>
      <table style="width:100%; border-collapse:collapse; font-size:12px;">
        <tr>
          <td style="padding:4px 0; color:#475569;">Facture de dépôt&nbsp;:</td>
          <td style="padding:4px 0; text-align:right; font-weight:600;">${factureDepot.numero_facture || '—'}</td>
        </tr>
        ${factureDepot.pourcentage_depot ? `
        <tr>
          <td style="padding:4px 0; color:#475569;">Pourcentage&nbsp;:</td>
          <td style="padding:4px 0; text-align:right; font-weight:600;">${factureDepot.pourcentage_depot}&nbsp;%</td>
        </tr>` : ''}
        <tr>
          <td style="padding:4px 0; color:#475569;">Montant du dépôt demandé&nbsp;:</td>
          <td style="padding:4px 0; text-align:right; font-weight:700; color:#1e3a8a;">${fmtCurrency(montantDepotDemande)}</td>
        </tr>
        ${montantDepotPaye > 0 ? `
        <tr>
          <td style="padding:4px 0; color:#15803d;">Montant reçu&nbsp;:</td>
          <td style="padding:4px 0; text-align:right; font-weight:700; color:#15803d;">${fmtCurrency(montantDepotPaye)}</td>
        </tr>` : ''}
        ${datePaiementDepot ? `
        <tr>
          <td style="padding:4px 0; color:#475569;">Date du paiement&nbsp;:</td>
          <td style="padding:4px 0; text-align:right;">${new Date(datePaiementDepot).toLocaleDateString('fr-CA')}</td>
        </tr>` : ''}
      </table>
    </div>` : '';

  // 🆕 [v8] Section ajouts (finale + complémentaire), avec descriptions visibles
  const sectionAjouts = (isFinaleOrComp && ajouts.length > 0) ? `
    <div style="border:1px solid #d97706; border-radius:8px; overflow:hidden; background:#fffbeb; margin-bottom:12px;">
      <div style="background:#fef3c7; padding:8px 14px; border-bottom:1px solid #fcd34d; font-weight:700; font-size:12px; color:#92400e; letter-spacing:0.05em; text-transform:uppercase;">Ajouts pendant les travaux</div>
      <div style="padding:12px 16px;">
      ${ajouts.map((ajout, i) => `
        <div style="margin-bottom:14px; ${i < ajouts.length - 1 ? 'padding-bottom:12px; border-bottom:1px dashed #fcd34d;' : ''}">
          ${ajout.description ? `<p style="font-weight:600; font-size:12px; margin-bottom:6px; color:#92400e;">${ajout.description}</p>` : ''}
          ${(ajout.items || []).length > 0 ? `
          <table style="width:100%; border-collapse:collapse;">
            <tbody>
              ${(ajout.items || []).map(item => `
              <tr>
                <td style="padding:4px 8px; border-bottom:1px solid #fde68a; font-size:11px;">${item.description || '—'}</td>
                <td style="padding:4px 8px; border-bottom:1px solid #fde68a; text-align:right; font-size:11px; font-weight:600;">${fmtCurrency(item.montant)}</td>
              </tr>`).join('')}
            </tbody>
          </table>` : ''}
          <p style="text-align:right; font-weight:700; font-size:12px; margin-top:6px; color:#92400e;">Total ajout : ${fmtCurrency(ajout.montant_total)}</p>
        </div>`).join('')}
      </div>
    </div>` : '';

  // Lignes totaux
  const rowTotaux = `
    <tr>
      <td style="padding:7px 12px; font-size:12px; border-bottom:1px solid #e5e7eb;">Sous-total</td>
      <td style="padding:7px 12px; font-size:12px; text-align:right; font-weight:600; border-bottom:1px solid #e5e7eb;">${fmtCurrency(facture.sous_total)}</td>
    </tr>
    ${hasRabais ? `
    <tr>
      <td style="padding:7px 12px; font-size:12px; color:#b91c1c; font-weight:600; border-bottom:1px solid #fecaca;">Rabais commercial</td>
      <td style="padding:7px 12px; font-size:12px; text-align:right; color:#b91c1c; font-weight:600; border-bottom:1px solid #fecaca;">− ${fmtCurrency(rabaisMontant)}</td>
    </tr>
    ${rabaisDescription ? `
    <tr>
      <td colspan="2" style="padding:2px 12px 6px; font-size:10px; font-style:italic; color:#6b7280; border-bottom:1px solid #e5e7eb;">${rabaisDescription}</td>
    </tr>` : ''}
    ` : ''}
    <tr>
      <td style="padding:7px 12px; font-size:12px; color:#6b7280; border-bottom:1px solid #e5e7eb;">TPS (5%)</td>
      <td style="padding:7px 12px; font-size:12px; text-align:right; color:#6b7280; border-bottom:1px solid #e5e7eb;">${fmtCurrency(facture.tps)}</td>
    </tr>
    <tr>
      <td style="padding:7px 12px; font-size:12px; color:#6b7280; border-bottom:2px solid #111827;">TVQ (9,975%)</td>
      <td style="padding:7px 12px; font-size:12px; text-align:right; color:#6b7280; border-bottom:2px solid #111827;">${fmtCurrency(facture.tvq)}</td>
    </tr>
    <tr>
      <td style="padding:10px 12px; font-size:15px; font-weight:700;">${facture.type_facture === 'depot' ? `DÉPÔT DEMANDÉ${facture.pourcentage_depot ? ` (${facture.pourcentage_depot}%)` : ''}` : 'TOTAL TRAVAUX'}</td>
      <td style="padding:10px 12px; font-size:15px; font-weight:700; text-align:right;">${fmtCurrency(facture.montant_total)}</td>
    </tr>
    ${isFinaleOrComp && totalAjouts > 0 ? `
    <tr>
      <td style="padding:7px 12px; font-size:12px; color:#b45309; border-bottom:1px solid #e5e7eb;">+ Ajouts pendant les travaux</td>
      <td style="padding:7px 12px; font-size:12px; text-align:right; color:#b45309; font-weight:600; border-bottom:1px solid #e5e7eb;">+ ${fmtCurrency(totalAjouts)}</td>
    </tr>` : ''}
    ${facture.type_facture === 'finale' && montantDepotASoustraire > 0 ? `
    <tr>
      <td style="padding:7px 12px; font-size:12px; color:#1d4ed8; border-bottom:1px solid #e5e7eb;">− Dépôt ${depotEstPaye ? 'reçu' : 'demandé (PRQC)'}</td>
      <td style="padding:7px 12px; font-size:12px; text-align:right; color:#1d4ed8; font-weight:600; border-bottom:1px solid #e5e7eb;">− ${fmtCurrency(montantDepotASoustraire)}</td>
    </tr>` : ''}
    ${facture.type_facture === 'finale' ? `
    <tr style="background:#f0fdf4;">
      <td style="padding:12px; font-size:16px; font-weight:800; color:#166534; border-top:2px solid #166534;">SOLDE DÛ</td>
      <td style="padding:12px; font-size:16px; font-weight:800; color:#166534; text-align:right; border-top:2px solid #166534;">${fmtCurrency(soldeFinalDu)}</td>
    </tr>` : ''}
  `;

  // Logos partenaires
  const sectionPartenaires = (infosEntreprise?.logos_partenaires?.length > 0) ? `
    <div style="border:1px solid #e5e7eb; border-radius:8px; padding:16px; margin-bottom:16px; text-align:center;">
      <p style="font-weight:700; margin-bottom:12px; font-size:13px;">Nos partenaires</p>
      <div style="display:flex; align-items:center; justify-content:center; gap:24px; flex-wrap:wrap;">
        ${infosEntreprise.logos_partenaires.map(l => `<img src="${API_URL}${l}" alt="Partenaire" style="height:50px; object-fit:contain; -webkit-print-color-adjust:exact;">`).join('')}
      </div>
    </div>` : '';

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>${titreFacture} – ${facture.numero_facture}</title>
  <style>
    @page { size: letter; margin: 0.65in 0.7in; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #111; line-height: 1.5; background: #f3f4f6; }
    .print-page { max-width: 800px; margin: 0 auto; background: white; padding: 24px; }
    table { width: 100%; border-collapse: collapse; }
    img { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .btn-print {
      display: inline-flex; align-items: center; gap: 8px;
      background: #2563eb; color: white; border: none; padding: 10px 20px;
      font-size: 14px; border-radius: 6px; cursor: pointer; font-weight: 600;
    }
    .btn-print:hover { background: #1d4ed8; }
    @media print {
      .no-print { display: none !important; }
      body { background: white; }
      .print-page { padding: 0; box-shadow: none; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    }
  </style>
</head>
<body>
  <!-- Barre impression (cachée à l'impression) -->
  <div class="no-print" style="background:#1e3a5f; color:white; padding:12px 24px; display:flex; align-items:center; justify-content:space-between; position:sticky; top:0; z-index:100;">
    <span style="font-size:14px; font-weight:600;">${titreFacture} – ${facture.numero_facture}</span>
    <button class="btn-print" onclick="window.print()">
      🖨️ Imprimer / Enregistrer PDF
    </button>
  </div>

  <div class="print-page">

    <!-- EN-TÊTE -->
    <div style="border:1px solid #e5e7eb; border-radius:8px; padding:20px; margin-bottom:16px;">
      <table>
        <tr>
          <!-- Logo -->
          <td style="width:22%; vertical-align:top; padding-right:16px;">
            ${logoUrl
              ? `<img src="${logoUrl}" alt="Logo" style="width:100%; max-width:160px; max-height:130px; object-fit:contain;">`
              : `<div style="width:150px; height:100px; background:#f3f4f6; border-radius:6px;"></div>`
            }
          </td>
          <!-- Infos entreprise -->
          <td style="width:50%; vertical-align:top; text-align:center; padding:4px 16px;">
            <p style="font-size:16px; font-weight:800; margin-bottom:8px;">${infosEntreprise?.nom_entreprise || ''}</p>
            <p style="font-size:11px; color:#374151;">${infosEntreprise?.adresse || ''}</p>
            <p style="font-size:11px; color:#374151;">${infosEntreprise?.ville || ''} ${infosEntreprise?.code_postal || ''}</p>
            <p style="font-size:12px; font-weight:700; margin-top:6px;">${infosEntreprise?.telephone || ''}</p>
            <p style="font-size:11px; color:#374151;">${infosEntreprise?.email || ''}</p>
          </td>
          <!-- Infos dossier -->
          <td style="width:28%; vertical-align:top; padding-left:8px;">
            <p style="font-size:13px; font-weight:700;">Dossier : ${dossier?.numero_dossier || ''}</p>
            <p style="margin-top:6px; font-size:11px;"><strong>Facture # :</strong> ${facture.numero_facture}</p>
            <p style="font-size:11px;"><strong>Date :</strong> ${new Date(facture.date_creation).toLocaleDateString('fr-CA')}</p>
            ${dossier?.representant_nom ? `<p style="font-size:11px;"><strong>Représentant :</strong> ${dossier.representant_nom}</p>` : ''}
            <div style="margin-top:10px; padding-top:8px; border-top:1px solid #e5e7eb;">
              ${infosEntreprise?.numero_tps ? `<p style="font-size:10px;"><strong>TPS :</strong> ${infosEntreprise.numero_tps}</p>` : ''}
              ${infosEntreprise?.numero_tvq ? `<p style="font-size:10px;"><strong>TVQ :</strong> ${infosEntreprise.numero_tvq}</p>` : ''}
              ${infosEntreprise?.numero_rbq ? `<p style="font-size:10px;"><strong>RBQ :</strong> ${infosEntreprise.numero_rbq}</p>` : ''}
            </div>
          </td>
        </tr>
      </table>

      <!-- Titre -->
      <h1 style="font-size:28px; font-weight:900; text-align:center; margin:20px 0; letter-spacing:1px;">${titreFacture}</h1>

      <!-- Client + Adresse facturation -->
      <table>
        <tr>
          <td style="width:50%; vertical-align:top; padding-right:20px;">
            <p style="font-weight:700; margin-bottom:8px; font-size:12px;">Client :</p>
            <p style="font-weight:700; font-size:13px;">${client?.nom || ''}</p>
            <p style="font-size:11px;">Téléphone : ${client?.telephone || ''}</p>
            ${client?.email ? `<p style="font-size:11px;">Courriel : ${client.email}</p>` : ''}
          </td>
          <td style="width:50%; vertical-align:top; padding-left:20px;">
            <p style="font-weight:700; margin-bottom:8px; font-size:12px;">Adresse de facturation :</p>
            <p style="font-size:11px;">${adresseFact}</p>
            <p style="font-size:11px;">${villeFact} ${cpFact}</p>
          </td>
        </tr>
      </table>
    </div>

    <!-- 🆕 [v8] SECTIONS DÉTAILLÉES : Peinture / Location -->
    ${sectionPeinture}
    ${sectionLocation}

    <!-- AJOUTS (facture finale / complémentaire) -->
    ${sectionAjouts}

    <!-- DÉPÔT PRQC (facture finale) -->
    ${sectionDepotPRQC}

    <!-- TOTAUX -->
    <div style="border:1px solid #e5e7eb; border-radius:8px; padding:20px; margin-bottom:16px;">
      <table style="max-width:360px; margin-left:auto;">
        <tbody>${rowTotaux}</tbody>
      </table>
    </div>

    <!-- MODALITÉS DE PAIEMENT -->
    <div style="border:1px solid #e5e7eb; border-radius:8px; padding:20px; margin-bottom:16px;">
      <h3 style="font-size:14px; font-weight:700; margin-bottom:14px;">Règlement de la facture</h3>
      <p style="font-weight:700; margin-bottom:6px; font-size:12px;">Virement Interac :</p>
      <p style="font-size:11px; line-height:1.7;">Pour acquitter cette facture, vous pouvez effectuer un virement Interac à l'adresse courriel de l'entreprise : <strong>info@peintureresidentielleqc.com</strong></p>
      <p style="font-size:11px; margin-top:4px;">Aucun mot de passe ne sera requis.</p>
      <p style="font-size:11px; margin-top:4px;">Nous comprenons que le paiement pourrait devoir être réparti sur plusieurs jours, selon la limite bancaire permise par votre institution.</p>
      <p style="font-weight:700; margin-top:14px; margin-bottom:6px; font-size:12px;">Chèque :</p>
      <p style="font-size:11px; line-height:1.7;">Vous pouvez acquitter cette facture par chèque en l'adressant à :</p>
      <p style="font-size:11px; margin-top:4px; line-height:1.8;">
        Peinture Résidentielle Qc Inc.<br>
        891, boulevard Charest Ouest, local 201<br>
        Québec (QC) G1N 2C9
      </p>
    </div>

    <!-- TERMES ET CONDITIONS -->
    <div style="border:1px solid #e5e7eb; border-radius:8px; padding:20px; margin-bottom:16px;">
      <h3 style="font-size:14px; font-weight:700; margin-bottom:12px;">Termes et conditions</h3>
      <ul style="list-style:none; padding:0; margin:0; font-size:11px; line-height:2;">
        <li style="padding:3px 0; border-bottom:1px dotted #e5e7eb;">• Tous les prix ci-haut mentionnés sont en dollars Canadiens.</li>
        <li style="padding:3px 0; border-bottom:1px dotted #e5e7eb;">• Paiement net 3 jours.</li>
        <li style="padding:3px 0;">• Frais d'administration de 2% par mois sur tout compte passé dû.</li>
      </ul>
    </div>

    <!-- REMERCIEMENTS -->
    <div style="border:1px solid #e5e7eb; border-radius:8px; padding:16px; margin-bottom:16px; background:#f0fdf4; text-align:center;">
      <p style="font-style:italic; color:#374151; font-weight:600; font-size:12px;">
        Nous vous remercions de la confiance que vous nous accordez pour la réalisation de vos travaux !
      </p>
    </div>

    <!-- LOGOS PARTENAIRES -->
    ${sectionPartenaires}

  </div>
</body>
</html>`;
}

async function ouvrirFacture(facture) {
  // 🆕 [Phase 63] PDF backend (Playwright headless) — rendu pixel-perfect
  // identique à ce que le client voit dans son navigateur.
  // 🆕 [Phase v13.37 — 2026-07-13] Preflight fetch pour afficher l'erreur détaillée
  // au lieu du générique "Erreur de connexion" de Chrome PDF viewer.
  const url = `${API_URL}/api/pdf/facture/${facture.id}`;
  try {
    const response = await fetch(url, { method: 'GET', credentials: 'include' });
    if (!response.ok) {
      // Backend a renvoyé un HTTPException (500) — extraire le detail JSON
      let detail = `HTTP ${response.status}`;
      try {
        const errJson = await response.json();
        detail = errJson.detail || detail;
      } catch (_) {
        // Body non JSON — garder le status
      }
      toast.error(`Erreur génération PDF facture : ${detail}`, { duration: 15000 });
      console.error('[ouvrirFacture] Backend a renvoyé une erreur :', detail);
      return;
    }
    // OK — ouvrir le PDF dans un nouvel onglet via blob URL
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    // Cleanup après 60s pour éviter la fuite mémoire (l'onglet a chargé le blob)
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  } catch (e) {
    toast.error(`Erreur réseau : ${e.message || 'connexion impossible'}`, { duration: 15000 });
    console.error('[ouvrirFacture] Fetch failed :', e);
  }
}

// ─── Modal de Création de Facture de Dépôt ─────────────────────────────────────
function CreerFactureDepotModal({ isOpen, onClose, dossier, client, infosEntreprise, onSuccess }) {
  const soumissionAcceptee = dossier?.soumissions?.find(s => s.statut === 'acceptee');
  const [depotType, setDepotType] = useState('pourcentage');
  const [depotValeur, setDepotValeur] = useState(25);
  const [adresseFacturation, setAdresseFacturation] = useState('');
  const [villeFacturation, setVilleFacturation] = useState('');
  const [codePostalFacturation, setCodePostalFacturation] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && client) {
      setAdresseFacturation(client.adresse_client || '');
      setVilleFacturation(client.ville_client || '');
      setCodePostalFacturation(client.code_postal_client || '');
    }
    if (isOpen && soumissionAcceptee) {
      setDepotType(soumissionAcceptee.depot_type || 'pourcentage');
      setDepotValeur(soumissionAcceptee.depot_valeur ?? 25);
    }
  }, [isOpen, client, soumissionAcceptee]);

  if (!soumissionAcceptee) return null;

  const totalAvecTaxes = soumissionAcceptee.montant_final || 0;
  const isCashDepot = dossier?.paiement_en_argent;
  // Pour cash: le % de dépôt est calculé sur le montant HT (ce que le client paie vraiment)
  const totalPourDepot = isCashDepot ? (soumissionAcceptee.montant_ht || totalAvecTaxes / 1.14975) : totalAvecTaxes;
  const montantDepotBrut = depotType === 'pourcentage'
    ? totalPourDepot * (depotValeur / 100)
    : depotValeur;
  const depotArrondi = Math.ceil(montantDepotBrut);
  const montantDepot = depotType === 'dollar' ? depotValeur : montantDepotBrut;
  const sousTotalDepot = montantDepot / 1.14975;
  const tpsDepot = sousTotalDepot * 0.05;
  const tvqDepot = sousTotalDepot * 0.09975;

  const handleArrondir = () => {
    setDepotType('dollar');
    setDepotValeur(depotArrondi);
  };

  const handleSubmit = async () => {
    if (!dossier || !client) return;
    setLoading(true);
    try {
      const items = (soumissionAcceptee.items || []).map(i => ({
        description: i.description || '',
        montant: i.montant || 0,
        type: i.type || 'service',
      }));
      const payload = {
        dossier_id: dossier.id,
        client_id: dossier.client_id,
        soumission_id: soumissionAcceptee.id || '',
        type_facture: 'depot',
        adresse_facturation: adresseFacturation,
        ville_facturation: villeFacturation,
        code_postal_facturation: codePostalFacturation,
        items,
        depot_type: depotType,
        depot_valeur: depotValeur,
        pourcentage_depot: depotType === 'pourcentage' ? depotValeur : Math.round(montantDepot / totalPourDepot * 100),
        sous_total: sousTotalDepot,
        tps: tpsDepot,
        tvq: tvqDepot,
        montant_total: montantDepot,
      };
      const res = await axios.post(`${API_URL}/api/crm/factures`, payload);
      toast.success('Facture de dépôt créée !');
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création de la facture');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Créer la Facture de Dépôt</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-3 text-sm">
            <p className="font-semibold">Soumission acceptée : {soumissionAcceptee.numero_soumission}</p>
            <p>Total soumission : <strong>{fmtCurrency(totalAvecTaxes)}</strong></p>
          </div>

          {/* Adresse de facturation */}
          <div>
            <Label className="font-semibold">Adresse de facturation</Label>
            <Input className="mt-1" placeholder="Adresse" value={adresseFacturation} onChange={e => setAdresseFacturation(e.target.value)} />
            <div className="grid grid-cols-2 gap-2 mt-1">
              <Input placeholder="Ville" value={villeFacturation} onChange={e => setVilleFacturation(e.target.value)} />
              <Input placeholder="Code postal" value={codePostalFacturation} onChange={e => setCodePostalFacturation(e.target.value)} />
            </div>
            <Button size="sm" variant="outline" className="mt-1 text-xs" onClick={() => {
              setAdresseFacturation(client?.adresse_client || '');
              setVilleFacturation(client?.ville_client || '');
              setCodePostalFacturation(client?.code_postal_client || '');
            }}>
              Utiliser l'adresse du client
            </Button>
          </div>

          {/* Dépôt */}
          <div>
            <Label className="font-semibold">Montant du dépôt</Label>
            <div className="flex items-center gap-2 mt-1">
              <select className="border rounded px-2 py-1.5 text-sm" value={depotType} onChange={e => setDepotType(e.target.value)}>
                <option value="pourcentage">%</option>
                <option value="dollar">$</option>
              </select>
              <MoneyInput value={depotValeur} onChange={v => setDepotValeur(v)} className="flex-1" />
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-blue-700 font-bold text-lg">Dépôt : {fmtCurrency(montantDepot)}</p>
              {depotType === 'pourcentage' && montantDepotBrut !== depotArrondi && (
                <Button size="sm" variant="outline" className="text-xs border-amber-400 text-amber-700" onClick={handleArrondir}>
                  <RotateCcw className="w-3 h-3 mr-1" />Arrondir → {fmtCurrency(depotArrondi)}
                </Button>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={handleSubmit} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? 'Création...' : 'Créer la Facture'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Modal Envoi Email Facture ─────────────────────────────────────────────────
// 🆕 [Phase 62 — 2026-06-17] Pièces jointes (photos/PDF/Word/Excel) :
//   - Upload depuis ordi OU sélection depuis fichiers du dossier client
//   - Compteur taille (max 25 Mo total) avec barre de progression
//   - Validation côté UI + serveur
const PHASE62_MAX_BYTES = 25 * 1024 * 1024;
const PHASE62_ALLOWED_EXTS = ['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'pdf', 'doc', 'docx', 'xls', 'xlsx'];

function iconForFilename(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'gif'].includes(ext)) return '📷';
  if (ext === 'pdf') return '📄';
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (['xls', 'xlsx'].includes(ext)) return '📊';
  return '📎';
}

function fmtBytes(n) {
  if (!n) return '0 Ko';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

function EnvoiFactureModal({ isOpen, onClose, facture, dossier, client, onSuccess }) {
  const [emailClient, setEmailClient] = useState('');
  const [sujet, setSujet] = useState('');
  const [contenu, setContenu] = useState('');
  const [_preview, setPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [piecesOrdi, setPiecesOrdi] = useState([]); // File[] depuis ordi
  const [piecesDossier, setPiecesDossier] = useState([]); // [{url, nom, taille, source: 'dossier'}]
  const [showPicker, setShowPicker] = useState(false);
  const [fichiersDossier, setFichiersDossier] = useState([]); // [{url, caption, ...}]
  const [loadingFichiers, setLoadingFichiers] = useState(false);
  // 🆕 [Phase 63 Bonus HOTFIX 2026-06-18] useState manquant — provoquait
  // ReferenceError: inclurePdfFacture is not defined (crash écran blanc onglet
  // Factures en prod). État ON par défaut (la coche est cochée à l'ouverture).
  const [inclurePdfFacture, setInclurePdfFacture] = useState(true);
  const { previewHtml, loadingPreview, fetchPreview } = useEmailPreview();

  // 🧱 Phase 4 — Lot C (D3) : le lien client porte un jeton opaque SANS expiration (POST …/lien-public ; régénérer = révoquer l'ancien).
  const [lienFactureJeton, setLienFactureJeton] = useState(null);
  useEffect(() => {
    if (!isOpen || !facture?.id) { setLienFactureJeton(null); return; }
    let actif = true;
    axios.post(`${API_URL}/api/crm/factures/${facture.id}/lien-public`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } })
      .then(r => { if (actif) setLienFactureJeton(r.data?.url || `${window.location.origin}${r.data?.chemin || `/facture/${facture.id}`}`); })
      .catch(() => { if (actif) { toast.error('Impossible de récupérer le lien client de la facture'); setLienFactureJeton(null); } });
    return () => { actif = false; };
  }, [isOpen, facture?.id]);
  // 🚨 Régénération EXPLICITE seulement (l'ancien lien ne fonctionnera plus) — jamais automatique à l'ouverture.
  const [confirmeRegen, setConfirmeRegen] = useState(false);
  const regenererLien = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/crm/factures/${facture.id}/lien-public/regenerer`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });
      setLienFactureJeton(r.data?.url); setConfirmeRegen(false);
      toast.success("Nouveau lien généré — l'ancien lien ne fonctionne plus.");
    } catch { toast.error('Impossible de régénérer le lien'); }
  };

  useEffect(() => {
    if (isOpen && lienFactureJeton) {
      setEmailClient(client?.email || '');
      const type = facture?.type_facture === 'depot' ? 'Facture de dépôt' : 'Facture finale';
      setSujet(`${type} – Dossier ${dossier?.numero_dossier}`);
      const typeLabel = facture?.type_facture === 'depot' ? 'dépôt' : 'finale';
      const montant = fmtCurrency(facture?.montant_total);
      const lienFacture = lienFactureJeton;
      // 🆕 [Phase 75 — 2026-06-21] Inclure les notes visibles client dans
      // l'email par défaut (encadré vert), si renseignées sur la facture.
      const notesClientHtml = (facture?.notes_client && facture.notes_client.trim())
        ? `\n<div style="background:#ecfdf5;border:2px solid #10b981;border-radius:8px;padding:14px;margin:14px 0;">` +
          `<p style="font-size:11px;font-weight:700;color:#065f46;margin:0 0 6px 0;text-transform:uppercase;letter-spacing:0.3px;">Notes</p>` +
          `<p style="font-size:13px;color:#064e3b;margin:0;white-space:pre-wrap;">${facture.notes_client.replace(/</g, '&lt;')}</p>` +
          `</div>\n`
        : '';
      setContenu(
        `Bonjour ${client?.nom || ''},\n\nVeuillez trouver ci-jointe votre facture de ${typeLabel} pour le dossier ${dossier?.numero_dossier}.\n\n` +
        `<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:20px;margin:16px 0;text-align:center;">` +
        `<p style="font-size:13px;color:#64748b;margin:0 0 8px 0;">Facture ${facture?.numero_facture}</p>` +
        `<p style="font-size:28px;font-weight:bold;color:#0f172a;margin:0 0 16px 0;">${montant}</p>` +
        `<a href="${lienFacture}" style="display:inline-block;background:#0f172a;color:white;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:bold;font-size:14px;">Consulter ma facture</a></div>\n\n` +
        notesClientHtml +
        `Nous vous remercions de la confiance que vous nous accordez pour la réalisation de vos travaux !\n\nPeinture Résidentielle Qc`
      );
      setPreview(false);
      // 🆕 Phase 62 — Réinitialise les pièces jointes à chaque ouverture
      setPiecesOrdi([]);
      setPiecesDossier([]);
      setShowPicker(false);
      // 🆕 Phase 63 Bonus — coche "Inclure PDF facture" toujours cochée à l'ouverture
      setInclurePdfFacture(true);
    }
  }, [isOpen, client, facture, dossier, lienFactureJeton]);

  // 🆕 [Phase 63 Bonus] Taille estimée du PDF de la facture (mesure runtime moyenne : ~15 ko)
  // Cette constante est volontairement conservatrice : un vrai PDF facture pèse 12-20 ko.
  // L'estimation est ajoutée au compteur UI mais le backend recalculera la vraie taille
  // et refusera si la limite 25 Mo est dépassée.
  const PHASE63_PDF_AUTO_ESTIMATED_BYTES = 50 * 1024; // 50 ko (estimation prudente)

  // 🆕 [Phase 62] Tailles cumulées (octets) — utilisée pour barre de progression + bloque envoi
  const tailleTotale =
    piecesOrdi.reduce((s, f) => s + (f.size || 0), 0) +
    piecesDossier.reduce((s, f) => s + (f.taille || 0), 0) +
    (inclurePdfFacture ? PHASE63_PDF_AUTO_ESTIMATED_BYTES : 0);
  const tailleDepasse = tailleTotale > PHASE62_MAX_BYTES;
  const pctTaille = Math.min(100, Math.round((tailleTotale / PHASE62_MAX_BYTES) * 100));

  // 🆕 [Phase 62] Ajout fichier(s) depuis l'ordi (multi-select)
  const handleAjouterDepuisOrdi = (e) => {
    const files = Array.from(e.target.files || []);
    const valides = [];
    const rejets = [];
    for (const f of files) {
      const ext = (f.name || '').split('.').pop().toLowerCase();
      if (!PHASE62_ALLOWED_EXTS.includes(ext)) {
        rejets.push(`${f.name} (format .${ext} non autorisé)`);
        continue;
      }
      valides.push(f);
    }
    if (rejets.length) toast.warning(`Fichier(s) ignoré(s) : ${rejets.join(', ')}`);
    setPiecesOrdi(prev => [...prev, ...valides]);
    e.target.value = ''; // reset input
  };

  // 🆕 [Phase 62] Charger la liste des fichiers déjà présents dans le dossier
  const handleOpenPicker = async () => {
    if (!dossier?.id) { toast.error('Dossier introuvable'); return; }
    setShowPicker(true);
    setLoadingFichiers(true);
    try {
      // Réutilise les photos du dossier (galerie) + photos_travail (chantier)
      const photos = (dossier.photos || []).map(p => ({ ...p, source: 'galerie' }));
      const photosTravail = (dossier.photos_travail || []).map(p => ({ ...p, source: 'chantier' }));
      setFichiersDossier([...photos, ...photosTravail]);
    } catch (e) {
      toast.error('Impossible de charger les fichiers du dossier');
    } finally {
      setLoadingFichiers(false);
    }
  };

  const togglePieceDossier = (item) => {
    const exists = piecesDossier.find(p => p.url === item.url);
    if (exists) {
      setPiecesDossier(prev => prev.filter(p => p.url !== item.url));
    } else {
      const filename = item.url?.split('/').pop() || 'fichier';
      // 🆕 Taille inconnue côté DB → on met 0, le backend calcule la vraie taille à l'envoi
      setPiecesDossier(prev => [...prev, { url: item.url, nom: item.caption || filename, taille: 0, source: 'dossier' }]);
    }
  };

  const handleSend = async () => {
    if (!emailClient) { toast.error('Courriel requis'); return; }
    if (tailleDepasse) { toast.error('Pièces jointes : taille totale > 25 Mo. Retirez des fichiers.'); return; }
    // 🆕 2026-04-30 — Avertir (sans bloquer) si l'envoi de la facture finale précède
    // le clic du bouton "Facture prête à envoyer" par la CP.
    if (facture?.type_facture === 'finale' && dossier?.statut === 'finalise') {
      const ok = window.confirm(
        "⚠️ La chargée de projet n'a pas encore cliqué sur \"Facture prête à envoyer\" depuis l'onglet Finalisé.\n\nVoulez-vous quand même envoyer la facture maintenant ? Le dossier passera directement en \"Attente paiement\"."
      );
      if (!ok) return;
    }
    setSending(true);
    try {
      // 🆕 [Phase 62] Envoi multipart (avec ou sans pièces jointes)
      const fd = new FormData();
      fd.append('dossier_id', dossier.id);
      fd.append('email_destinataire', emailClient);
      fd.append('sujet', sujet);
      fd.append('contenu', contenu);
      piecesOrdi.forEach(f => fd.append('pieces_jointes', f, f.name));
      fd.append('pieces_jointes_dossier', JSON.stringify(piecesDossier.map(p => p.url)));
      fd.append('inclure_pdf_facture', String(inclurePdfFacture)); // 🆕 Phase 63 Bonus
      const res = await axios.post(
        `${API_URL}/api/crm/factures/${facture.id}/envoyer-avec-pieces`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data?.warning) toast.warning(res.data.warning);
      const nbPieces = res.data?.nb_pieces_jointes ?? 0;
      toast.success(nbPieces > 0 ? `Facture envoyée avec ${nbPieces} pièce(s) jointe(s) !` : 'Facture envoyée !');
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Envoyer la Facture par Courriel</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border rounded-lg p-3 bg-slate-50 text-xs" data-testid="facture-lien-actif-bloc">
            <p className="font-medium text-slate-700">Lien client actif (reste valide tant que vous ne le régénérez pas)</p>
            <p className="break-all text-slate-600 mt-1" data-testid="facture-lien-actif">{lienFactureJeton || 'Lien indisponible'}</p>
            {!confirmeRegen ? (
              <button type="button" className="mt-2 text-red-700 underline" onClick={() => setConfirmeRegen(true)} data-testid="facture-lien-regenerer">Régénérer le lien (l'ancien ne fonctionnera plus)</button>
            ) : (
              <span className="mt-2 inline-flex items-center gap-2">
                <span className="text-red-700">Confirmer ? Tout lien déjà envoyé au client cessera de fonctionner.</span>
                <Button size="sm" variant="destructive" className="h-6 px-2 text-xs" onClick={regenererLien} data-testid="facture-lien-regenerer-confirmer">Oui, régénérer</Button>
                <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => setConfirmeRegen(false)} data-testid="facture-lien-regenerer-annuler">Annuler</Button>
              </span>
            )}
          </div>
          <div>
            <Label>Courriel du client</Label>
            <Input value={emailClient} onChange={e => setEmailClient(e.target.value)} placeholder="client@email.com" data-testid="facture-envoi-email" />
          </div>
          <div>
            <Label>Sujet</Label>
            <Input value={sujet} onChange={e => setSujet(e.target.value)} data-testid="facture-envoi-sujet" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Message</Label>
              <div className="flex gap-0">
                <button type="button" className={`px-3 py-1 rounded-t border text-xs font-medium ${!_preview ? 'bg-white border-b-white font-semibold' : 'bg-gray-100 text-gray-500'}`} onClick={() => setPreview(false)}>Modifier</button>
                <button type="button" className={`px-3 py-1 rounded-t border text-xs font-medium ${_preview ? 'bg-white border-b-white font-semibold' : 'bg-gray-100 text-gray-500'}`} onClick={() => { setPreview(true); fetchPreview(contenu); }} data-testid="facture-envoi-apercu-tab">Aperçu</button>
              </div>
            </div>
            {_preview ? (
              loadingPreview ? (
                <div className="border rounded-b-md rounded-tr-md p-6 min-h-[200px] flex items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
              ) : (
                <EmailPreviewFrame html={previewHtml} minHeight={360} />
              )
            ) : (
              <textarea value={contenu} onChange={e => setContenu(e.target.value)} rows={10} className="w-full rounded-b-md rounded-tr-md border px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Contenu du courriel..." data-testid="facture-envoi-contenu" />
            )}
          </div>

          {/* 🆕 [Phase 62 — 2026-06-17] Section pièces jointes ───────────────────── */}
          <div data-testid="facture-pieces-jointes-section" className="border rounded-lg p-3 bg-slate-50 border-slate-200">
            {/* 🆕 [Phase 63 Bonus] Coche "Inclure le PDF de la facture" ─────────── */}
            <label className="flex items-start gap-2 mb-3 cursor-pointer select-none p-2 bg-white border border-slate-200 rounded">
              <input
                type="checkbox"
                checked={inclurePdfFacture}
                onChange={(e) => setInclurePdfFacture(e.target.checked)}
                className="mt-0.5 w-4 h-4 cursor-pointer accent-blue-600"
                data-testid="facture-pj-inclure-pdf-coche"
              />
              <span className="text-sm">
                <span className="font-semibold text-slate-800">📎 Inclure le PDF de la facture</span>
                <span className="text-xs text-slate-500 ml-2">(généré automatiquement, ~50 ko)</span>
              </span>
            </label>

            <div className="flex items-center justify-between mb-3">
              <Label className="font-semibold text-slate-700">Pièces jointes additionnelles (optionnel)</Label>
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.heic,.heif,.webp,.pdf,.doc,.docx,.xls,.xlsx"
                    className="hidden"
                    onChange={handleAjouterDepuisOrdi}
                    data-testid="facture-pj-input-ordi"
                  />
                  <Button type="button" variant="outline" size="sm" asChild data-testid="facture-pj-btn-ordi">
                    <span><Upload className="w-4 h-4 mr-1.5" /> Depuis mon ordi</span>
                  </Button>
                </label>
                <Button type="button" variant="outline" size="sm" onClick={handleOpenPicker} data-testid="facture-pj-btn-dossier">
                  <FileText className="w-4 h-4 mr-1.5" /> Depuis le dossier
                </Button>
              </div>
            </div>

            {/* Liste pièces jointes */}
            {(piecesOrdi.length > 0 || piecesDossier.length > 0) ? (
              <ul className="space-y-1.5 mb-3" data-testid="facture-pj-liste">
                {piecesOrdi.map((f, i) => (
                  <li key={`ordi-${i}`} className="flex items-center justify-between bg-white border rounded px-2.5 py-1.5 text-sm" data-testid={`facture-pj-item-ordi-${i}`}>
                    <div className="flex items-center gap-2 truncate">
                      <span className="text-lg shrink-0">{iconForFilename(f.name)}</span>
                      <span className="truncate">{f.name}</span>
                      <span className="text-xs text-slate-500 shrink-0">({fmtBytes(f.size)})</span>
                    </div>
                    <button type="button" onClick={() => setPiecesOrdi(prev => prev.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700" aria-label="Retirer" data-testid={`facture-pj-remove-ordi-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
                {piecesDossier.map((p, i) => (
                  <li key={`dos-${i}`} className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded px-2.5 py-1.5 text-sm" data-testid={`facture-pj-item-dossier-${i}`}>
                    <div className="flex items-center gap-2 truncate">
                      <span className="text-lg shrink-0">{iconForFilename(p.nom)}</span>
                      <span className="truncate">{p.nom}</span>
                      <Badge variant="outline" className="text-[10px] bg-blue-100 border-blue-300 text-blue-700">dossier</Badge>
                    </div>
                    <button type="button" onClick={() => setPiecesDossier(prev => prev.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700" aria-label="Retirer" data-testid={`facture-pj-remove-dossier-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-500 italic mb-3">Aucune pièce jointe sélectionnée. Limite : 25 Mo total, formats JPG/PNG/HEIC/PDF/Word/Excel.</p>
            )}

            {/* Compteur taille + barre progression */}
            <div className="flex items-center justify-between text-xs mb-1">
              <span className={tailleDepasse ? 'text-red-600 font-semibold' : 'text-slate-600'} data-testid="facture-pj-compteur">
                {fmtBytes(tailleTotale)} / 25 Mo
                {inclurePdfFacture && (
                  <span className="text-slate-400 ml-1">(inclut PDF facture ~{fmtBytes(PHASE63_PDF_AUTO_ESTIMATED_BYTES)})</span>
                )}
              </span>
              {tailleDepasse && <span className="text-red-600 font-semibold">⚠️ Limite dépassée</span>}
            </div>
            <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${tailleDepasse ? 'bg-red-500' : pctTaille > 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${pctTaille}%` }}
                data-testid="facture-pj-progress"
              />
            </div>
          </div>

          {/* Modal sélecteur fichiers du dossier */}
          <Dialog open={showPicker} onOpenChange={setShowPicker}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Sélectionner des fichiers du dossier</DialogTitle>
              </DialogHeader>
              {loadingFichiers ? (
                <p className="text-sm text-slate-500 py-8 text-center">Chargement...</p>
              ) : fichiersDossier.length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center italic">Aucun fichier dans ce dossier.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="facture-pj-picker-grid">
                  {fichiersDossier.map((item, i) => {
                    const selected = !!piecesDossier.find(p => p.url === item.url);
                    return (
                      <button
                        key={`${item.url}-${i}`}
                        type="button"
                        onClick={() => togglePieceDossier(item)}
                        className={`relative border-2 rounded-lg p-2 text-left transition ${selected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-400 bg-white'}`}
                        data-testid={`facture-pj-picker-item-${i}`}
                      >
                        {item.url && /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(item.url) ? (
                          <img src={urlFichier(item.url)} alt={item.caption || 'photo'} className="w-full h-24 object-cover rounded" />
                        ) : (
                          <div className="w-full h-24 flex items-center justify-center bg-slate-100 rounded">
                            <span className="text-4xl">{iconForFilename(item.url || '')}</span>
                          </div>
                        )}
                        <p className="text-xs mt-1 truncate text-slate-700">{item.caption || (item.url?.split('/').pop() || 'fichier')}</p>
                        <p className="text-[10px] text-slate-400 uppercase">{item.source || 'galerie'}</p>
                        {selected && (
                          <div className="absolute top-1 right-1 w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs">✓</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              <DialogFooter className="mt-2">
                <Button variant="outline" onClick={() => setShowPicker(false)}>Fermer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={handleSend} disabled={sending || tailleDepasse} className={facture?.email_envoye ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} data-testid="facture-envoi-btn-send">
              <Send className="w-4 h-4 mr-2" />{sending ? 'Envoi...' : 'Envoyer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Modal Historique & Ajout de Paiements ─────────────────────────────────────
const METHODES_PAIEMENT = [
  { value: 'virement', label: 'Virement Interac' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'comptant', label: 'Comptant' },
  { value: 'carte', label: 'Carte de crédit/débit' },
  { value: 'autre', label: 'Autre' },
];

function PaiementsModal({ isOpen, onClose, facture, ajouts = [], allFactures = [], onSuccess, dossier }) {
  const [montant, setMontant] = useState(0);
  const [methode, setMethode] = useState('virement');
  const [notes, setNotes] = useState('');
  const [datePaiement, setDatePaiement] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [silencieux, setSilencieux] = useState(false);

  // Calcul du solde réel pour la facture finale
  const factureDepot = allFactures.find(f => f.type_facture === 'depot');
  const montantDepotPaye = facture?.type_facture === 'finale'
    ? (factureDepot?.montant_paye_reel || factureDepot?.montant_paye || 0)
    : 0;
  // 🆕 [Phase v13.42 — 2026-07-14] Les ajouts sont propagés aux soldes des factures
  // finales ET complémentaires (avant : uniquement finale, provoquant le bug solde=0
  // après ajout sur une complémentaire payée).
  const totalAjouts = (facture?.type_facture === 'finale' || facture?.type_facture === 'complementaire')
    ? ajouts.reduce((s, a) => s + (a.montant_total || 0), 0)
    : 0;
  // Pour les dossiers cash: solde basé sur montant_avant_taxes
  const montantFacture = dossier?.paiement_en_argent
    ? (facture?.montant_avant_taxes || facture?.montant_total || 0)
    : (facture?.montant_total || 0);
  // 🆕 [Phase v13.42] Facture finale : base + ajouts − dépôt. Facture complémentaire : base + ajouts (pas de dépôt). Autres : base seulement.
  const montantBase = (facture?.type_facture === 'finale')
    ? montantFacture + totalAjouts - montantDepotPaye
    : (facture?.type_facture === 'complementaire')
      ? montantFacture + totalAjouts
      : montantFacture;
  const totalDejaPaye = (facture?.paiements || []).reduce((s, p) => s + (p.montant || 0), 0);
  const soldeCourant = Math.max(0, montantBase - totalDejaPaye);

  useEffect(() => {
    if (isOpen && facture) {
      setMontant(soldeCourant); // Pré-remplir avec le solde restant
      setMethode('virement');
      setNotes('');
      setDatePaiement(new Date().toISOString().split('T')[0]);
    }
  }, [isOpen, facture?.id, soldeCourant]);

  const handleAddPaiement = async () => {
    if (!montant || montant <= 0) { toast.error('Montant invalide'); return; }
    setLoading(true);
    try {
      const dateISO = datePaiement ? new Date(datePaiement + 'T12:00:00').toISOString() : '';
      const params = new URLSearchParams({ montant, methode, notes, silencieux: silencieux.toString(), date_paiement: dateISO });
      const { data } = await axios.post(`${API_URL}/api/crm/factures/${facture.id}/ajouter-paiement?${params}`);
      toast.success(`Paiement de ${fmtCurrency(montant)} enregistré !`);
      if (data.statut_paiement === 'paye') toast.success('Facture entièrement payée !');
      onSuccess();
      setMontant(Math.max(0, soldeCourant - montant));
      setNotes('');
    } catch { toast.error('Erreur lors de l\'enregistrement'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (paiementId) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    try {
      await axios.delete(`${API_URL}/api/crm/factures/${facture.id}/paiements/${paiementId}`);
      toast.success('Paiement supprimé');
      onSuccess();
    } catch { toast.error('Erreur'); }
  };

  if (!facture) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-emerald-600" />
            Paiements – {facture.numero_facture}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Résumé financier */}
          <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1 border">
            <div className="flex justify-between"><span className="text-gray-600">Montant total facturé</span><span className="font-medium">{fmtCurrency(montantBase)}</span></div>
            <div className="flex justify-between text-green-700"><span>Total reçu</span><span className="font-medium">{fmtCurrency(totalDejaPaye)}</span></div>
            <div className={`flex justify-between font-bold border-t pt-1 ${soldeCourant > 0 ? 'text-red-600' : 'text-green-600'}`}>
              <span>Solde restant</span>
              <span>{fmtCurrency(soldeCourant)}</span>
            </div>
          </div>

          {/* Historique des paiements */}
          {(facture.paiements || []).length > 0 && (
            <div>
              <Label className="font-semibold text-sm">Historique des paiements</Label>
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                {[...(facture.paiements || [])].reverse().map((p) => (
                  <div key={p.id} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-green-700">{fmtCurrency(p.montant)}</span>
                        <Badge className="bg-blue-100 text-blue-700 text-xs font-normal">{METHODES_PAIEMENT.find(m => m.value === p.methode)?.label || p.methode}</Badge>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {new Date(p.date).toLocaleDateString('fr-CA', { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}
                        {p.notes && <span className="ml-2 italic">{p.notes}</span>}
                      </div>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7 ml-2" onClick={() => handleDelete(p.id)}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ajouter un paiement */}
          {soldeCourant > 0 && (
            <div className="border rounded-lg p-3 space-y-3 bg-white">
              <Label className="font-semibold text-sm">Enregistrer un paiement</Label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Montant reçu</Label>
                  <MoneyInput value={montant} onChange={setMontant} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Date du paiement</Label>
                  <Input
                    type="date"
                    value={datePaiement}
                    onChange={e => setDatePaiement(e.target.value)}
                    className="mt-1"
                    data-testid="paiement-date-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Méthode</Label>
                  <select
                    className="w-full border rounded-md px-3 py-2 text-sm mt-1 bg-white"
                    value={methode}
                    onChange={e => setMethode(e.target.value)}
                  >
                    {METHODES_PAIEMENT.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes (optionnel)</Label>
                <Input
                  className="mt-1"
                  placeholder="Ex: Paiement #1 de 4, limite bancaire…"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2 py-1">
                <input
                  data-testid="silencieux-toggle"
                  type="checkbox"
                  id="mode-silencieux"
                  checked={silencieux}
                  onChange={e => setSilencieux(e.target.checked)}
                  className="rounded border-slate-300"
                />
                <label htmlFor="mode-silencieux" className="text-xs text-slate-600 cursor-pointer">
                  Mode silencieux (aucun SMS envoyé au client — pour import de données)
                </label>
              </div>
              {montant > 0 && montant < soldeCourant && (
                <p className="text-xs text-amber-600">
                  Solde après ce paiement : <strong>{fmtCurrency(soldeCourant - montant)}</strong>
                </p>
              )}
              <Button
                onClick={handleAddPaiement}
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {loading ? 'Enregistrement...' : `Enregistrer ${fmtCurrency(montant)}`}
              </Button>
            </div>
          )}

          {soldeCourant <= 0 && (
            <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg p-3 border border-green-200">
              <CheckCircle className="w-5 h-5" />
              <span className="font-semibold">Facture entièrement payée !</span>
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={onClose}>Fermer</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Modal Créer/Modifier Ajout ─────────────────────────────────────────────────
function AjoutModal({ isOpen, onClose, dossier, ajoutToEdit, onSuccess }) {
  const [description, setDescription] = useState('');
  const [itemsServices, setItemsServices] = useState([{ description: '', montant: 0 }]);
  const [itemsProduits, setItemsProduits] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🆕 2026-05-15 — Si ajoutToEdit ne contient QUE `__complementaire_facture_id`,
  // c'est un NOUVEL ajout à rattacher à une facture complémentaire (pas un edit).
  const isNewLinkedToComp = ajoutToEdit && !ajoutToEdit.id && ajoutToEdit.__complementaire_facture_id;
  const isEditing = ajoutToEdit && ajoutToEdit.id;
  const linkedComplementaireFactureId = ajoutToEdit?.__complementaire_facture_id || ajoutToEdit?.complementaire_facture_id || null;

  useEffect(() => {
    if (isOpen) {
      if (isEditing) {
        setDescription(ajoutToEdit.description || '');
        const svcs = (ajoutToEdit.items || []).filter(i => i.type !== 'produit');
        const prods = (ajoutToEdit.items || []).filter(i => i.type === 'produit');
        setItemsServices(svcs.length ? svcs : [{ description: '', montant: 0 }]);
        setItemsProduits(prods);
      } else {
        setDescription('');
        setItemsServices([{ description: '', montant: 0 }]);
        setItemsProduits([]);
      }
    }
  }, [isOpen, ajoutToEdit, isEditing]);

  const totalServices = itemsServices.reduce((s, i) => s + (i.montant || 0), 0);
  const totalProduits = itemsProduits.reduce((s, i) => s + (i.montant || 0), 0);
  const sousTotal = totalServices + totalProduits;
  const tps = sousTotal * 0.05;
  const tvq = sousTotal * 0.09975;
  const total = sousTotal + tps + tvq;

  const handleSubmit = async () => {
    if (!description) { toast.error('Description requise'); return; }
    setLoading(true);
    try {
      const allItems = [
        ...itemsServices.filter(i => i.description).map(i => ({ ...i, type: 'service' })),
        ...itemsProduits.map(i => ({ ...i, type: 'produit' })),
      ];
      const payload = {
        dossier_id: dossier.id,
        client_id: dossier.client_id,
        description,
        items: allItems,
        sous_total: sousTotal,
        tps,
        tvq,
        montant_total: total,
        // 🆕 2026-05-15 — Si rattaché à une facture complémentaire
        ...(linkedComplementaireFactureId ? { complementaire_facture_id: linkedComplementaireFactureId } : {}),
        ...(isEditing ? {
          id: ajoutToEdit.id,
          date_creation: ajoutToEdit.date_creation,
          signature_demandee: ajoutToEdit.signature_demandee,
          signature_obtenue: ajoutToEdit.signature_obtenue,
          statut: ajoutToEdit.statut,
          token_public: ajoutToEdit.token_public,
        } : {})
      };
      const url = isEditing ? `${API_URL}/api/crm/ajouts/${ajoutToEdit.id}` : `${API_URL}/api/crm/ajouts`;
      const method = isEditing ? 'put' : 'post';
      const { data: result } = await axios[method](url, payload);
      toast.success(isEditing ? 'Ajout modifié !' : (isNewLinkedToComp ? 'Ajout complémentaire créé !' : 'Ajout créé !'));
      
      // Si le backend propose des heures supplémentaires pour le BT
      if (result.heures_proposition && !isEditing) {
        const hp = result.heures_proposition;
        const accept = window.confirm(
          `Ajout de ${hp.montant_ajout_ht?.toLocaleString('fr-CA', {style:'currency',currency:'CAD'})} (HT)\n\n` +
          `Heures supplémentaires proposées : +${hp.heures_supplementaires}h\n` +
          `Heures actuelles : ${hp.heures_actuelles}h → ${hp.heures_nouvelles}h\n\n` +
          `Voulez-vous ajouter ces heures au bon de travail ?`
        );
        if (accept) {
          try {
            await axios.post(`${API_URL}/api/crm/bons-travail/${hp.bon_id}/ajouter-heures`, { heures_supplementaires: hp.heures_supplementaires });
            toast.success(`+${hp.heures_supplementaires}h ajoutées au bon de travail (${hp.heures_actuelles}h → ${hp.heures_nouvelles}h)`);
            
            // 🛡️ 2026-05 — CASCADE DÉSACTIVÉE (cause majeure de déplacement aléatoire des BT).
            // Plus de window.confirm ici — l'utilisateur déclenche la cascade explicitement
            // depuis la page Calendrier de Production si besoin.
          } catch { toast.error('Erreur ajout heures au BT'); }
        }
      }
      
      onSuccess();
      onClose();
    } catch {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{ajoutToEdit ? "Modifier l'Ajout" : 'Nouvel Ajout au Contrat'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Description générale */}
          <div>
            <Label>Description de l'ajout</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Ex: Travaux supplémentaires — Peinture du plafond..." className="mt-1" rows={2} />
          </div>

          {/* Sections Services + Produits */}
          <Tabs defaultValue="services">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="services" className="flex items-center gap-2">
                <Wrench className="w-4 h-4" />
                Services
                {totalServices > 0 && <Badge variant="secondary" className="ml-1 text-xs">{itemsServices.filter(i => i.description).length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="produits" className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Produits
                {itemsProduits.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{itemsProduits.length}</Badge>}
              </TabsTrigger>
            </TabsList>

            {/* Onglet Services */}
            <TabsContent value="services" className="mt-3">
              <div className="space-y-2">
                {itemsServices.map((item, idx) => (
                  // ✅ FIX 2026-04-27: clé stable (pas JSON.stringify qui fait perdre le focus à chaque keystroke)
                  <div key={item.id || item._id || `service-${idx}`} className="flex items-center gap-2">
                    <Input
                      placeholder="Description du service"
                      value={item.description}
                      onChange={e => {
                        const n = [...itemsServices]; n[idx].description = e.target.value; setItemsServices(n);
                      }}
                      className="flex-1"
                    />
                    <MoneyInput
                      value={item.montant}
                      onChange={v => {
                        const n = [...itemsServices]; n[idx].montant = v; setItemsServices(n);
                      }}
                      className="w-28"
                    />
                    <span className="text-sm text-gray-500">$</span>
                    <Button size="icon" variant="ghost" onClick={() => setItemsServices(itemsServices.filter((_, i) => i !== idx))}>
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => setItemsServices([...itemsServices, { description: '', montant: 0 }])}>
                  <Plus className="w-4 h-4 mr-1" />Ajouter un service
                </Button>
              </div>
              {totalServices > 0 && (
                <p className="text-right text-sm font-semibold text-blue-700 mt-2">Sous-total services : {fmtCurrency(totalServices)}</p>
              )}
            </TabsContent>

            {/* Onglet Produits */}
            <TabsContent value="produits" className="mt-3">
              <SectionProduits
                items={itemsProduits}
                onItemsChange={setItemsProduits}
              />
              {totalProduits > 0 && (
                <p className="text-right text-sm font-semibold text-amber-700 mt-2">Sous-total produits : {fmtCurrency(totalProduits)}</p>
              )}
            </TabsContent>
          </Tabs>

          {/* Totaux */}
          {(totalServices > 0 || totalProduits > 0) && (
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1 border">
              {totalServices > 0 && <div className="flex justify-between text-gray-600"><span>Services</span><span>{fmtCurrency(totalServices)}</span></div>}
              {totalProduits > 0 && <div className="flex justify-between text-gray-600"><span>Produits</span><span>{fmtCurrency(totalProduits)}</span></div>}
              <div className="flex justify-between border-t pt-1"><span>Sous-total</span><span>{fmtCurrency(sousTotal)}</span></div>
              <div className="flex justify-between text-gray-500"><span>TPS (5%)</span><span>{fmtCurrency(tps)}</span></div>
              <div className="flex justify-between text-gray-500"><span>TVQ (9,975%)</span><span>{fmtCurrency(tvq)}</span></div>
              <div className="flex justify-between font-bold text-base border-t pt-1"><span>TOTAL</span><span>{fmtCurrency(total)}</span></div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={handleSubmit} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? 'Sauvegarde...' : (ajoutToEdit ? 'Modifier' : 'Créer')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Modal Demander Signature Ajout ─────────────────────────────────────────────
function DemanderSignatureModal({ isOpen, onClose, ajout, dossier, client, factures, onSuccess }) {
  const [emailClient, setEmailClient] = useState('');
  const [sujet, setSujet] = useState('');
  const [contenu, setContenu] = useState('');
  const [_preview, setPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const { previewHtml, loadingPreview, fetchPreview } = useEmailPreview();
  const factureDepot = factures?.find(f => f.type_facture === 'depot');
  const montantDepotPaye = factureDepot?.montant_paye_reel || factureDepot?.montant_paye || 0;

  useEffect(() => {
    if (isOpen) {
      setEmailClient(client?.email || '');
      setSujet(`Ajout au contrat – Dossier ${dossier?.numero_dossier} – Approbation requise`);
      setContenu(
        `Bonjour ${client?.nom || ''},\n\nUn ajout à votre contrat a été effectué pour le dossier ${dossier?.numero_dossier}.\n\n` +
        `Description : ${ajout?.description}\nMontant de l'ajout : ${fmtCurrency(ajout?.montant_total)}\n` +
        (montantDepotPaye > 0 ? `\nDépôt déjà reçu : ${fmtCurrency(montantDepotPaye)}\n` : '') +
        `\nMerci de nous confirmer votre approbation.\n\nNous vous remercions de la confiance que vous nous accordez !`
      );
      setPreview(false);
    }
  }, [isOpen, client, ajout, dossier, montantDepotPaye]);

  const handleSend = async () => {
    if (!emailClient) { toast.error('Courriel requis'); return; }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/crm/ajouts/${ajout.id}/demander-signature`, { dossier_id: dossier.id, ajout_id: ajout.id, email_destinataire: emailClient, sujet, contenu });
      toast.success('Email de signature envoyé !');
      onSuccess();
      onClose();
    } catch {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Demander une Signature pour l'Ajout</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-amber-50 rounded p-3 text-sm">
            <p><strong>Ajout :</strong> {ajout?.description}</p>
            <p><strong>Montant :</strong> {fmtCurrency(ajout?.montant_total)}</p>
            {montantDepotPaye > 0 && <p className="mt-1 text-blue-700"><strong>Dépôt déjà reçu :</strong> {fmtCurrency(montantDepotPaye)}</p>}
          </div>
          <div>
            <Label>Courriel du client</Label>
            <Input value={emailClient} onChange={e => setEmailClient(e.target.value)} placeholder="client@email.com" data-testid="ajout-envoi-email" />
          </div>
          <div>
            <Label>Sujet</Label>
            <Input value={sujet} onChange={e => setSujet(e.target.value)} data-testid="ajout-envoi-sujet" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Message</Label>
              <div className="flex gap-0">
                <button type="button" className={`px-3 py-1 rounded-t border text-xs font-medium ${!_preview ? 'bg-white border-b-white font-semibold' : 'bg-gray-100 text-gray-500'}`} onClick={() => setPreview(false)}>Modifier</button>
                <button type="button" className={`px-3 py-1 rounded-t border text-xs font-medium ${_preview ? 'bg-white border-b-white font-semibold' : 'bg-gray-100 text-gray-500'}`} onClick={() => { setPreview(true); fetchPreview(contenu); }} data-testid="ajout-envoi-apercu-tab">Aperçu</button>
              </div>
            </div>
            {_preview ? (
              loadingPreview ? (
                <div className="border rounded-b-md rounded-tr-md p-6 min-h-[200px] flex items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
              ) : (
                <EmailPreviewFrame html={previewHtml} minHeight={360} />
              )
            ) : (
              <textarea value={contenu} onChange={e => setContenu(e.target.value)} rows={10} className="w-full rounded-b-md rounded-tr-md border px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Contenu du courriel..." data-testid="ajout-envoi-contenu" />
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={handleSend} disabled={sending} className="bg-blue-600 hover:bg-blue-700" data-testid="ajout-envoi-btn-send">
              <Send className="w-4 h-4 mr-2" />{sending ? 'Envoi...' : 'Envoyer pour signature'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── (Phase 74) Composant ModifierFactureDialog supprimé ─────────────────────
// Avant Phase 74, un mini-dialog (montant + notes + numéro) gérait la modification.
// Désormais, le bouton « Modifier » ouvre directement `EditeurFacture` avec la
// prop `factureExistante` → éditeur complet (lignes, taxes, rabais, notes client, etc.).

// ─── Carte Facture ──────────────────────────────────────────────────────────────
function CarteFacture({ facture, dossier, client, infosEntreprise, ajouts, allFactures, onUpdate }) {
  const [showEnvoi, setShowEnvoi] = useState(false);
  const [showPaiements, setShowPaiements] = useState(false);
  const [showModifier, setShowModifier] = useState(false);
  // 🆕 [Phase 92 — 2026-07-08] Dialog rappel SMS + historique SMS
  const [showRappelSms, setShowRappelSms] = useState(false);
  const [smsLogs, setSmsLogs] = useState(null);
  const { isProprietaire, user } = useAuth();
  const peutModifier = isProprietaire || ['charge_projet', 'adjoint_admin'].includes(user?.role);

  const estDepot = facture.type_facture === 'depot';
  const factureDepot = allFactures.find(f => f.type_facture === 'depot');
  const montantDepotPaye = estDepot ? 0 : (factureDepot?.montant_paye_reel || factureDepot?.montant_paye || 0);
  const totalAjouts = !estDepot ? ajouts.reduce((s, a) => s + (a.montant_total || 0), 0) : 0;
  // Pour les dossiers cash: solde basé sur montant_avant_taxes
  const montantFacture = dossier?.paiement_en_argent
    ? (facture.montant_avant_taxes || facture.montant_total || 0)
    : (facture.montant_total || 0);
  const montantBase = !estDepot
    ? montantFacture + totalAjouts - montantDepotPaye
    : montantFacture;
  const totalPaye = (facture.paiements || []).reduce((s, p) => s + (p.montant || 0), 0) || facture.montant_paye || 0;
  const soldeDu = Math.max(0, montantBase - totalPaye);

  const paiements = facture.paiements || [];

  // 🆕 [Phase 92 — 2026-07-08] Charge l'historique SMS pour cette facture (si non payée uniquement pour perf)
  useEffect(() => {
    if (facture.statut_paiement === 'paye') { setSmsLogs([]); return; }
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/sms/logs-facture/${facture.id}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
        );
        if (!cancel) setSmsLogs(data?.logs || []);
      } catch {
        if (!cancel) setSmsLogs([]);
      }
    })();
    return () => { cancel = true; };
  }, [facture.id, facture.statut_paiement]);

  const historiqueEnvois = facture.historique_envois || [];
  const soldeDuAffiche = soldeDu;

  return (
    <>
      <Card className="hover:shadow-md transition-shadow border-l-4" style={{ borderLeftColor: estDepot ? '#3b82f6' : '#10b981' }}>
        <CardContent className="pt-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-bold text-lg">{facture.numero_facture}</span>
                <Badge variant="outline">{estDepot ? 'Dépôt' : 'Finale'}</Badge>
                <Badge className={STATUT_COLORS[facture.statut_paiement]}>{STATUT_LABELS[facture.statut_paiement]}</Badge>
                {facture.email_envoye && (
                  <Badge className="bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />Envoyée
                  </Badge>
                )}
                {facture.email_envoye && <BadgeSuiviClient suivi={facture.suivi_client} testid={`facture-${facture.id}-suivi`} />}
              </div>
              <p className="text-xs text-gray-500">Créée le {new Date(facture.date_creation).toLocaleDateString('fr-CA')}</p>
              {facture.suivi_client?.vu && <div className="mt-2"><BlocSuiviClient suivi={facture.suivi_client} testid={`facture-${facture.id}-bloc-suivi`} /></div>}
            </div>
            <div className="text-right">
              <p className="font-bold text-xl">{fmtCurrency(facture.montant_total)}</p>
              {!estDepot && (totalAjouts > 0 || montantDepotPaye > 0) && (
                <p className="text-sm text-green-700 font-semibold">Solde : {fmtCurrency(soldeDu)}</p>
              )}
            </div>
          </div>

          {/* Breakdown facture finale */}
          {!estDepot && (
            <div className="bg-gray-50 rounded p-2 text-xs mb-3 space-y-1">
              <div className="flex justify-between"><span>Base (soumission)</span><span>{fmtCurrency(facture.montant_total)}</span></div>
              {totalAjouts > 0 && <div className="flex justify-between text-amber-700"><span>+ Ajouts</span><span>{fmtCurrency(totalAjouts)}</span></div>}
              {montantDepotPaye > 0 && <div className="flex justify-between text-blue-700"><span>- Dépôt reçu</span><span>- {fmtCurrency(montantDepotPaye)}</span></div>}
              <div className={`flex justify-between font-bold border-t pt-1 ${soldeDu > 0 ? 'text-red-600' : 'text-green-600'}`}>
                <span>Solde dû</span><span>{fmtCurrency(soldeDu)}</span>
              </div>
            </div>
          )}

          {/* Mini historique paiements */}
          {paiements.length > 0 && (
            <div className="mb-3 space-y-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Paiements reçus</p>
              {paiements.map((p, i) => (
                <div key={p.id || i} className="flex items-center justify-between text-xs bg-green-50 rounded px-2 py-1">
                  <span className="text-green-700 font-semibold">{fmtCurrency(p.montant)}</span>
                  <span className="text-gray-500">{METHODES_PAIEMENT.find(m => m.value === p.methode)?.label || p.methode}</span>
                  <span className="text-gray-400">{new Date(p.date).toLocaleDateString('fr-CA')}</span>
                  {p.notes && <span className="text-gray-400 italic truncate max-w-20">{p.notes}</span>}
                </div>
              ))}
              <div className="flex justify-between text-xs font-bold px-2">
                <span className="text-green-700">Total reçu : {fmtCurrency(totalPaye)}</span>
                {soldeDu > 0 && <span className="text-red-600">Restant : {fmtCurrency(soldeDu)}</span>}
              </div>
            </div>
          )}

          {/* 🆕 [Phase 92 — 2026-07-08] Historique unifié envois (email + SMS rappels) */}
          {(historiqueEnvois.length > 0 || (smsLogs?.length || 0) > 0) && (
            <div className="mb-3 space-y-1" data-testid={`historique-envois-facture-${facture.id}`}>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Historique envois ({historiqueEnvois.length + (smsLogs?.length || 0)})
              </p>
              {historiqueEnvois.slice(-3).map((e, i) => (
                <div key={`env-${i}`} className="flex items-center justify-between text-xs bg-blue-50 rounded px-2 py-1">
                  <span className="text-blue-700 font-semibold">
                    {e.mode === 'sms' ? '📱 SMS rappel' : '📧 Courriel'}
                  </span>
                  <span className="text-gray-500 truncate max-w-28">{e.destinataire || '—'}</span>
                  <span className="text-gray-400">
                    {e.date ? new Date(e.date).toLocaleDateString('fr-CA') : '—'}
                  </span>
                </div>
              ))}
              {(smsLogs || []).slice(0, 5).map((s, i) => (
                <div key={`sms-${s.id || i}`} className="flex items-center justify-between text-xs bg-orange-50 rounded px-2 py-1"
                  data-testid={`sms-item-${s.id || i}`}>
                  <span className="text-orange-700 font-semibold">📱 {s.context?.type || 'sms'}</span>
                  <Badge className={s.status === 'envoye' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}
                    variant="outline" data-testid={`sms-status-${s.id || i}`}>
                    {s.status === 'envoye' ? '✓' : '✗'} {s.status || '—'}
                  </Badge>
                  <span className="text-gray-400">
                    {s.sent_at ? new Date(s.sent_at).toLocaleDateString('fr-CA') + ' ' + new Date(s.sent_at).toLocaleTimeString('fr-CA', {hour: '2-digit', minute: '2-digit'}) : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => ouvrirFacture(facture)} data-testid={`btn-visualiser-pdf-${facture.id}`}>
              <Printer className="w-4 h-4 mr-1" />Visualiser en PDF
            </Button>
            <Button
              size="sm"
              variant={facture.email_envoye ? 'default' : 'outline'}
              className={facture.email_envoye ? 'bg-green-600 hover:bg-green-700' : ''}
              onClick={() => setShowEnvoi(true)}
            >
              <Send className="w-4 h-4 mr-1" />{facture.email_envoye ? 'Renvoyer' : 'Envoyer'}
            </Button>
            {facture.statut_paiement !== 'paye' && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowPaiements(true)}>
                <CreditCard className="w-4 h-4 mr-1" />
                {paiements.length > 0 ? `Paiements (${paiements.length})` : 'Enregistrer paiement'}
              </Button>
            )}
            {/* 🆕 [Phase 92 — 2026-07-08] Bouton rappel SMS — uniquement si balance restante > 0 */}
            {facture.statut_paiement !== 'paye' && soldeDuAffiche > 0.01 && (
              <Button
                size="sm"
                variant="outline"
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                onClick={() => setShowRappelSms(true)}
                data-testid={`btn-rappel-sms-facture-${facture.id}`}
              >
                <span className="mr-1">📱</span>Rappel SMS
                {(smsLogs?.length || 0) > 0 && (
                  <span className="ml-1.5 text-xs bg-orange-100 rounded px-1.5">{smsLogs.length}</span>
                )}
              </Button>
            )}
            {facture.statut_paiement === 'paye' && paiements.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => setShowPaiements(true)}>
                <CreditCard className="w-4 h-4 mr-1" />Historique ({paiements.length})
              </Button>
            )}
            {/* Bouton Avis Google : facture finale payée uniquement */}
            {facture.type_facture === 'finale' && facture.statut_paiement === 'paye' && (
              <AvisGoogleButton dossier={dossier} />
            )}
            {/* Bouton Modifier — toujours visible pour les admin */}
            {peutModifier && (
              <Button size="sm" variant="outline" className="text-blue-700 border-blue-200 hover:bg-blue-50"
                onClick={() => setShowModifier(true)} data-testid={`btn-modifier-facture-${facture.id}`}>
                <Edit className="w-3.5 h-3.5 mr-1" />Modifier
              </Button>
            )}
            {/* Bouton Supprimer — toujours visible pour les admin */}
            {peutModifier && (
              <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" data-testid={`btn-supprimer-facture-${facture.id}`}
                onClick={async () => {
                  if (!window.confirm('Êtes-vous certain de vouloir supprimer cette facture ?')) return;
                  try {
                    await axios.delete(`${API_URL}/api/crm/factures/${facture.id}`);
                    toast.success('Facture supprimée');
                    onUpdate();
                  } catch { toast.error('Erreur suppression'); }
                }}>
                <Trash2 className="w-3.5 h-3.5 mr-1" />Supprimer
              </Button>
            )}
          </div>
          <VersionsEnvoyees factureId={facture.id} rafraichir={facture.updated_at || facture.date_envoi} />  {/* 📦 Phase 6 */}
        </CardContent>
      </Card>

      <EnvoiFactureModal isOpen={showEnvoi} onClose={() => setShowEnvoi(false)} facture={facture} dossier={dossier} client={client} onSuccess={onUpdate} />
      <PaiementsModal isOpen={showPaiements} onClose={() => setShowPaiements(false)} facture={facture} ajouts={ajouts} allFactures={allFactures} onSuccess={onUpdate} dossier={dossier} />
      {/* 🆕 [Phase 92 — 2026-07-08] Dialog rappel SMS de paiement */}
      <RappelPaiementSmsDialog
        isOpen={showRappelSms}
        onClose={() => setShowRappelSms(false)}
        facture={facture}
        onSuccess={() => { setShowRappelSms(false); onUpdate(); }}
      />
      {/* 🆕 [Phase 74 — 2026-06-21] Bouton "Modifier" ouvre désormais l'éditeur complet
          via `factureExistante`, plutôt que l'ancien mini-dialog limité (3 champs).
          L'éditeur fait un PUT (mode édition) avec l'ensemble des champs : items,
          taxes, rabais, adresse facturation, notes_client (Phase 75), etc. */}
      <EditeurFacture
        isOpen={showModifier}
        onClose={() => setShowModifier(false)}
        dossier={dossier}
        client={client}
        infosEntreprise={infosEntreprise}
        typeFacture={facture.type_facture || 'depot'}
        factureDepot={factureDepot}
        ajouts={ajouts}
        factureExistante={facture}
        complementaireNumero={facture.numero_complementaire || null}
        onSuccess={() => { setShowModifier(false); onUpdate(); }}
      />
    </>
  );
}

// ─── Onglet Principal Factures ──────────────────────────────────────────────────
export function OngletFactures({ dossier, onUpdate }) {
  const [factures, setFactures] = useState([]);
  const [ajouts, setAjouts] = useState([]);
  const [client, setClient] = useState(null);
  const [infosEntreprise, setInfosEntreprise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paiementArgent, setPaiementArgent] = useState(dossier?.paiement_en_argent || false);
  const [montantPartieArgent, setMontantPartieArgent] = useState(dossier?.montant_partie_argent || 0);
  const [editingMontantArgent, setEditingMontantArgent] = useState(false);
  // 🆕 [Phase v13.44.1 — 2026-07-14] Liste des BT du dossier pour désactiver
  // préventivement le bouton "Créer facture finale" si ≥1 BT non terminé (UX préventive).
  const [bonsTravail, setBonsTravail] = useState([]);

  const [showCreerDepot, setShowCreerDepot] = useState(false);
  const [showCreerFinale, setShowCreerFinale] = useState(false);
  const [showAjoutModal, setShowAjoutModal] = useState(false);
  const [ajoutToEdit, setAjoutToEdit] = useState(null);
  const [showDemanderSignature, setShowDemanderSignature] = useState(false);
  const [ajoutPourSignature, setAjoutPourSignature] = useState(null);

  const togglePaiementArgent = async () => {
    const newVal = !paiementArgent;
    try {
      await axios.patch(`${API_URL}/api/crm/dossiers/${dossier.id}/paiement-argent`, {
        paiement_en_argent: newVal,
        montant_partie_argent: newVal ? montantPartieArgent : 0,
      });
      setPaiementArgent(newVal);
      if (!newVal) setMontantPartieArgent(0);
      toast.success(newVal ? 'Contrat marqué : paiement en argent comptant' : 'Paiement en argent retiré');
      onUpdate();
    } catch { toast.error("Erreur réseau"); }
  };

  const saveMontantArgent = async (newMontant) => {
    const val = Math.max(0, parseFloat(newMontant) || 0);
    try {
      await axios.patch(`${API_URL}/api/crm/dossiers/${dossier.id}/paiement-argent`, {
        paiement_en_argent: val > 0,
        montant_partie_argent: val,
      });
      setMontantPartieArgent(val);
      setPaiementArgent(val > 0);
      setEditingMontantArgent(false);
      toast.success(val > 0 ? `Partie argent : ${val.toFixed(2)}$` : 'Partie argent retirée');
      onUpdate();
    } catch { toast.error("Erreur réseau"); }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [facturesRes, ajoutsRes, infosRes, btRes] = await Promise.all([
        axios.get(`${API_URL}/api/crm/factures?dossier_id=${dossier.id}`),
        axios.get(`${API_URL}/api/crm/ajouts?dossier_id=${dossier.id}`),
        axios.get(`${API_URL}/api/entreprise/infos`),
        // 🆕 [Phase v13.44.1] Fetch des BT du dossier pour désactiver
        // préventivement le bouton "Créer facture finale" si non terminé.
        axios.get(`${API_URL}/api/crm/bons-travail?dossier_id=${dossier.id}`)
          .catch(() => ({ data: { bons_travail: [] } })),
      ]);
      setFactures(facturesRes.data.factures || []);
      setAjouts(ajoutsRes.data.ajouts || []);
      setInfosEntreprise(infosRes.data);
      setBonsTravail(btRes.data?.bons_travail || btRes.data || []);
      if (dossier.client_id) {
        const { data: clientData } = await axios.get(`${API_URL}/api/crm/clients/${dossier.client_id}`);
        setClient(clientData);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (dossier?.id) fetchAll(); }, [dossier?.id]);

  const handleUpdate = () => { fetchAll(); onUpdate(); };

  const factureDepot = factures.find(f => f.type_facture === 'depot');
  const factureFinale = factures.find(f => f.type_facture === 'finale');
  // 🆕 [Phase v13.44.1 — 2026-07-14] BT actifs (non annulés) qui ne sont pas encore
  // marqués comme terminés — utilisé pour désactiver préventivement le bouton
  // "Créer la Facture Finale" et éviter l'aller-retour serveur (409).
  const btsNonTermines = (bonsTravail || []).filter(bt =>
    bt.statut !== 'annule' && bt.statut !== 'termine' && bt.termine !== true
  );
  const finaleBloquee = btsNonTermines.length > 0;
  const finaleTooltip = finaleBloquee
    ? `Marque tous les bons de travail comme terminés avant de créer la facture finale. En attente : ${btsNonTermines.map(b => b.numero_bon || b.id?.slice(0, 6)).join(', ')}`
    : '';
  // 🆕 2026-05-15 — Factures complémentaires (créées après la finale pour
  // les ajouts post-réception). Triées par numéro_complementaire croissant.
  const facturesComplementaires = factures
    .filter(f => f.type_facture === 'complementaire')
    .sort((a, b) => (a.numero_complementaire || 0) - (b.numero_complementaire || 0));
  // Ajouts pendant les travaux = ajouts SANS complementaire_facture_id
  const ajoutsPendantTravaux = ajouts.filter(a => !a.complementaire_facture_id);
  const soumissionAcceptee = dossier?.soumissions?.find(s => s.statut === 'acceptee');
  // Dialog state pour la facture complémentaire
  const [showEditeurComp, setShowEditeurComp] = useState(false);

  // 📊 V1/V2 — approbation / refus d'un ajout (date_approbation + acteur ; refus avec motif)
  const handleStatutAjout = async (ajoutId, statut) => {
    let motif = null;
    if (statut === 'refuse') {
      motif = window.prompt("Motif du refus (visible dans l'historique) :");
      if (motif === null) return;
    } else if (!window.confirm("Approuver cet ajout ? Il comptera dans le CA signé du mois d'aujourd'hui.")) return;
    try {
      await axios.post(`${API_URL}/api/crm/ajouts/${ajoutId}/statut`, { statut, motif });
      toast.success(statut === 'approuve' ? 'Ajout approuvé' : 'Ajout refusé');
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible de changer le statut de l'ajout"); }
  };

  // 📊 V2 — lier / délier un ajout à une facture du dossier
  const handleLierFacture = async (ajoutId, factureId) => {
    try {
      await axios.post(`${API_URL}/api/crm/ajouts/${ajoutId}/facture`, { facture_id: factureId || null });
      toast.success(factureId ? 'Ajout lié à la facture' : 'Lien facture retiré');
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Impossible de lier la facture'); }
  };

  const handleDeleteAjout = async (ajoutId) => {
    if (!window.confirm('Supprimer cet ajout ? (suppression logique : il reste consultable dans l\'historique, mais est exclu des calculs)')) return;
    try {
      await axios.delete(`${API_URL}/api/crm/ajouts/${ajoutId}`);
      toast.success('Ajout supprimé');
      handleUpdate();
    } catch { toast.error('Erreur suppression'); }
  };

  const handleCreerFactureFinale = async () => {
    if (!soumissionAcceptee) { toast.error('Aucune soumission acceptée'); return; }
    try {
      const totalAjouts = ajouts.reduce((s, a) => s + (a.montant_total || 0), 0);
      const baseTotalTTC = soumissionAcceptee.montant_final || 0;
      const montantHT = soumissionAcceptee.montant_ht || (baseTotalTTC / 1.14975);
      const isCash = dossier?.paiement_en_argent;
      
      const payload = {
        dossier_id: dossier.id,
        client_id: dossier.client_id,
        soumission_id: soumissionAcceptee.id || '',
        type_facture: 'finale',
        adresse_facturation: factureDepot?.adresse_facturation || client?.adresse_client || '',
        ville_facturation: factureDepot?.ville_facturation || client?.ville_client || '',
        code_postal_facturation: factureDepot?.code_postal_facturation || client?.code_postal_client || '',
        items: (soumissionAcceptee.items || []).map(i => ({ description: i.description || '', montant: i.montant || 0, type: i.type || 'service' })),
        sous_total: montantHT,
        tps: montantHT * 0.05,
        tvq: montantHT * 0.09975,
        montant_total: baseTotalTTC,  // TTC — montant affiché au client
        montant_avant_taxes: montantHT,  // HT — utilisé pour le solde interne des contrats cash
        montant_ajouts: totalAjouts,
        montant_depot_paye: factureDepot?.montant_paye_reel || factureDepot?.montant_paye || 0,
        paiement_en_argent: isCash,
      };
      await axios.post(`${API_URL}/api/crm/factures`, payload);
      toast.success('Facture finale créée !');
      handleUpdate();
    } catch (err) {
      // 🆕 [Phase v13.44 — 2026-07-14] Gérer HTTP 409 "travaux non terminés"
      // avec toast détaillé + liste des BT concernés.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === 'object' && detail.code === 'bt_non_termine') {
        toast.error(detail.message || 'Travaux non terminés', {
          description: 'Marque les bons de travail comme terminés avant de créer la facture finale.',
          duration: 8000,
          'data-testid': 'toast-bt-non-termine',
        });
      } else {
        toast.error('Erreur création facture finale');
      }
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Chargement...</div>;

  return (
    <div className="space-y-6">

      {/* ── Bandeau Paiement en argent comptant ── */}
      <div
        data-testid="toggle-paiement-argent"
        className={`rounded-lg border transition-colors ${paiementArgent ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50 border-gray-200'}`}
      >
        <div
          className="flex items-center justify-between gap-4 p-3 cursor-pointer"
          onClick={togglePaiementArgent}
        >
          <div className="flex items-center gap-2">
            <Banknote className={`w-5 h-5 ${paiementArgent ? 'text-yellow-600' : 'text-gray-400'}`} />
            <div>
              <p className={`text-sm font-semibold ${paiementArgent ? 'text-yellow-800' : 'text-gray-700'}`}>
                {paiementArgent ? 'Paiement en argent comptant' : 'Paiement standard (chèque / virement / carte)'}
              </p>
              <p className="text-xs text-gray-400">Cliquez pour {paiementArgent ? 'désactiver' : 'activer'} le mode paiement en argent</p>
            </div>
          </div>
          <div className={`w-10 h-6 rounded-full transition-colors flex items-center px-0.5 ${paiementArgent ? 'bg-yellow-400' : 'bg-gray-300'}`}>
            <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${paiementArgent ? 'translate-x-4' : 'translate-x-0'}`} />
          </div>
        </div>

        {paiementArgent && (
          <div className="border-t border-yellow-200 px-3 py-2 bg-yellow-50/70">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <p className="text-xs font-semibold text-yellow-900">💵 Montant payé en argent</p>
                <p className="text-[11px] text-yellow-700">
                  {montantPartieArgent > 0
                    ? `La partie restante (${((dossier?.montant_soumission || 0) - montantPartieArgent).toFixed(2)}$ + taxes) sera facturée normalement.`
                    : 'Laisser à 0 pour un contrat 100% en argent (comportement actuel).'}
                </p>
              </div>
              {editingMontantArgent ? (
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    defaultValue={montantPartieArgent}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { saveMontantArgent(e.target.value); }
                      if (e.key === 'Escape') setEditingMontantArgent(false);
                    }}
                    onBlur={(e) => saveMontantArgent(e.target.value)}
                    className="w-28 px-2 py-1 border border-yellow-400 rounded text-sm font-bold text-yellow-900 bg-white"
                    data-testid="input-montant-argent"
                  />
                  <span className="text-sm font-bold text-yellow-900">$</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setEditingMontantArgent(true); }}
                  className="px-3 py-1.5 bg-white border border-yellow-400 rounded text-sm font-bold text-yellow-900 hover:bg-yellow-100"
                  data-testid="btn-edit-montant-argent"
                >
                  {montantPartieArgent > 0 ? `${montantPartieArgent.toFixed(2)} $` : '— $'}  ✏️
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Section 1 : Facture de Dépôt ── */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              Facture de Dépôt
            </CardTitle>
            {!factureDepot && soumissionAcceptee && (
              <Button onClick={() => setShowCreerDepot(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />Créer la Facture de Dépôt
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!soumissionAcceptee ? (
            <div className="text-center py-8 text-gray-400">
              <FileText className="w-10 h-10 mx-auto mb-2" />
              <p>Aucune soumission acceptée. Acceptez d'abord une soumission pour créer des factures.</p>
            </div>
          ) : !factureDepot ? (
            <div className="text-center py-8 text-gray-400">
              <DollarSign className="w-10 h-10 mx-auto mb-2" />
              <p>Aucune facture de dépôt. Cliquez sur "Créer la Facture de Dépôt" pour commencer.</p>
            </div>
          ) : (
            <CarteFacture facture={factureDepot} dossier={dossier} client={client} infosEntreprise={infosEntreprise} ajouts={ajouts} allFactures={factures} onUpdate={handleUpdate} />
          )}
        </CardContent>
      </Card>

      {/* ── Section 2 : Ajouts pendant les travaux ── */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-600" />
              Ajouts pendant les travaux
            </CardTitle>
            {soumissionAcceptee && (
              <Button variant="outline" onClick={() => { setAjoutToEdit(null); setShowAjoutModal(true); }} className="border-amber-400 text-amber-700 hover:bg-amber-50">
                <Plus className="w-4 h-4 mr-2" />Ajouter
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {ajoutsPendantTravaux.length === 0 ? (
            <div className="text-center py-6 text-gray-400 text-sm">Aucun ajout pendant les travaux pour ce dossier.</div>
          ) : (
            <div className="space-y-3">
              {ajoutsPendantTravaux.map(ajout => (
                <Card key={ajout.id} className={`border-l-4 ${ajout.signature_obtenue ? 'border-l-green-500' : ajout.signature_demandee ? 'border-l-amber-500' : 'border-l-gray-300'}`}>
                  <CardContent className="pt-3 pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{ajout.description}</p>
                        {ajout.items?.length > 0 && (
                          <ul className="text-xs text-gray-500 mt-1 space-y-0.5">
                            {ajout.items.map((item, i) => (
                              <li key={item.id || item._id || i} className="flex justify-between">
                                <span>{item.description}</span>
                                <span>{fmtCurrency(item.montant)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="font-bold text-base">{fmtCurrency(ajout.montant_total)}</span>
                          {ajout.signature_obtenue && (
                            <Badge className="bg-green-100 text-green-800 text-xs cursor-pointer" title="Cliquer pour voir la signature" onClick={(e) => {
                              e.stopPropagation();
                              if (ajout.signature_data) {
                                const w = window.open('', '_blank', 'width=500,height=400');
                                w.document.write(`<html><head><title>Signature — ${ajout.description}</title></head><body style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;margin:0;font-family:Arial;background:#f9fafb;"><h3>Signature: ${ajout.signature_nom || 'Client'}</h3><img src="${ajout.signature_data}" style="max-width:400px;border:1px solid #ddd;border-radius:8px;background:white;padding:8px;" /><p style="color:#6b7280;font-size:13px;margin-top:12px;">${ajout.date_signature ? new Date(ajout.date_signature).toLocaleDateString('fr-CA') : ''}</p></body></html>`);
                              } else { toast.info('Signature non disponible'); }
                            }}>
                              <CheckCircle className="w-3 h-3 mr-1" />Signé
                            </Badge>
                          )}
                          {ajout.signature_demandee && !ajout.signature_obtenue && (
                            <Badge className="bg-amber-100 text-amber-800 text-xs">Signature demandée</Badge>
                          )}
                          {/* 📊 V1 — statut d'approbation visible + date/acteur (mois d'acceptation du CA signé) */}
                          {ajout.statut === 'approuve' && <Badge className="bg-emerald-100 text-emerald-800 text-xs" data-testid={`ajout-${ajout.id}-statut`}>Approuvé{ajout.date_approbation ? ` le ${new Date(ajout.date_approbation).toLocaleDateString('fr-CA')}` : ''}{ajout.approuve_par?.email ? ` par ${ajout.approuve_par.email}` : ''}</Badge>}
                          {ajout.statut === 'refuse' && <Badge className="bg-red-100 text-red-800 text-xs" data-testid={`ajout-${ajout.id}-statut`} title={ajout.motif_refus || ''}>Refusé{ajout.motif_refus ? ` — ${ajout.motif_refus}` : ''}</Badge>}
                          {(!ajout.statut || ajout.statut === 'en_attente') && <Badge variant="outline" className="text-xs text-slate-600" data-testid={`ajout-${ajout.id}-statut`}>En attente d'approbation</Badge>}
                          {/* 📊 V2 — lien facture */}
                          <select className="text-xs border rounded px-1 py-0.5 bg-white" value={ajout.facture_id || ''} data-testid={`ajout-${ajout.id}-facture`} onChange={(e) => handleLierFacture(ajout.id, e.target.value)} title="Facture liée">
                            <option value="">Aucune facture liée</option>
                            {factures.map(f => <option key={f.id} value={f.id}>{f.numero_facture || f.id}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-1 ml-3">
                        {ajout.statut !== 'approuve' && (
                          <Button size="sm" variant="outline" className="text-xs border-emerald-500 text-emerald-700" data-testid={`ajout-${ajout.id}-approuver`} onClick={() => handleStatutAjout(ajout.id, 'approuve')}>
                            <CheckCircle className="w-3 h-3 mr-1" />Approuver
                          </Button>
                        )}
                        {ajout.statut !== 'refuse' && ajout.statut !== 'approuve' && (
                          <Button size="sm" variant="outline" className="text-xs border-red-400 text-red-700" data-testid={`ajout-${ajout.id}-refuser`} onClick={() => handleStatutAjout(ajout.id, 'refuse')}>Refuser</Button>
                        )}
                        {!ajout.signature_obtenue && (
                          <Button size="sm" variant="outline" className="text-xs border-blue-400 text-blue-700" onClick={() => { setAjoutPourSignature(ajout); setShowDemanderSignature(true); }}>
                            <PenLine className="w-3 h-3 mr-1" />Signature
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => { setAjoutToEdit(ajout); setShowAjoutModal(true); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDeleteAjout(ajout.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <div className="flex justify-end gap-4">
                <p className="text-sm text-emerald-700" data-testid="ajouts-total-approuves">Approuvés : {fmtCurrency(ajoutsPendantTravaux.filter((a) => a.statut === 'approuve' || a.signature_obtenue).reduce((s, a) => s + (a.montant_total || 0), 0))}</p>
                <p className="text-sm font-bold text-amber-700">Total ajouts pendant travaux : {fmtCurrency(ajoutsPendantTravaux.reduce((s, a) => s + (a.montant_total || 0), 0))}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Section 3 : Facture Finale ── */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Facture Finale
            </CardTitle>
            {!factureFinale && factureDepot && (
              <Button
                onClick={() => setShowCreerFinale(true)}
                disabled={finaleBloquee}
                title={finaleTooltip}
                aria-label={finaleTooltip || 'Créer la Facture Finale'}
                className={finaleBloquee ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}
                data-testid="btn-creer-facture-finale"
              >
                <Plus className="w-4 h-4 mr-2" />Créer la Facture Finale
              </Button>
            )}
            {finaleBloquee && !factureFinale && factureDepot && (
              <span className="text-xs text-amber-600 ml-2 italic" data-testid="msg-bt-non-termines">
                ⓘ {btsNonTermines.length} bon(s) de travail à terminer
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!factureDepot ? (
            <div className="text-center py-6 text-gray-400 text-sm">Créez d'abord la facture de dépôt.</div>
          ) : !factureFinale ? (
            <div className="text-center py-8 text-gray-400">
              <CheckCircle className="w-10 h-10 mx-auto mb-2" />
              <p>Cliquez sur "Créer la Facture Finale" quand les travaux sont terminés.</p>
            </div>
          ) : (
            <CarteFacture facture={factureFinale} dossier={dossier} client={client} infosEntreprise={infosEntreprise} ajouts={ajoutsPendantTravaux} allFactures={factures} onUpdate={handleUpdate} />
          )}
        </CardContent>
      </Card>

      {/* ── 🆕 2026-05-15 — Section 4 : Factures Complémentaires ── */}
      {/* Visible UNIQUEMENT après que la facture finale soit créée (peu importe le statut de paiement). */}
      {factureFinale && (
        <Card className="border-purple-200">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-purple-600" />
                Factures complémentaires
                {facturesComplementaires.length > 0 && (
                  <Badge className="bg-purple-100 text-purple-800 text-xs">{facturesComplementaires.length}</Badge>
                )}
              </CardTitle>
              <Button onClick={() => setShowEditeurComp(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-creer-facture-complementaire">
                <Plus className="w-4 h-4 mr-2" />Nouvelle facture complémentaire
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {facturesComplementaires.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm">
                <p>Aucune facture complémentaire.</p>
                <p className="text-xs mt-1 text-gray-400">À utiliser pour les ajouts faits APRÈS l'envoi de la facture finale (ex : retour client, retouches, ajouts post-réception).</p>
              </div>
            ) : (
              <div className="space-y-4">
                {facturesComplementaires.map((fc) => {
                  // Ajouts liés à cette facture complémentaire (post-finale)
                  const ajoutsLies = ajouts.filter(a => a.complementaire_facture_id === fc.id);
                  return (
                    <div key={fc.id} className="space-y-2">
                      <CarteFacture
                        facture={fc}
                        dossier={dossier}
                        client={client}
                        infosEntreprise={infosEntreprise}
                        ajouts={ajoutsLies}
                        allFactures={factures}
                        onUpdate={handleUpdate}
                      />
                      {/* Ajouts complémentaires liés */}
                      <div className="pl-4 border-l-2 border-purple-200 ml-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-purple-700 uppercase">Ajouts complémentaires liés</p>
                          <Button size="sm" variant="outline" className="border-purple-300 text-purple-700 text-xs h-7" onClick={() => { setAjoutToEdit({ __complementaire_facture_id: fc.id }); setShowAjoutModal(true); }} data-testid={`btn-add-ajout-comp-${fc.id}`}>
                            <Plus className="w-3 h-3 mr-1" />Ajout complémentaire
                          </Button>
                        </div>
                        {ajoutsLies.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">Aucun ajout complémentaire.</p>
                        ) : (
                          ajoutsLies.map(a => (
                            <div key={a.id} className="text-xs flex justify-between border-l-2 border-purple-300 pl-2 py-1">
                              <span>{a.description}</span>
                              <span className="font-semibold">{fmtCurrency(a.montant_total)}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      <EditeurFacture isOpen={showCreerDepot} onClose={() => setShowCreerDepot(false)} dossier={dossier} client={client} infosEntreprise={infosEntreprise} onSuccess={handleUpdate} typeFacture="depot" soumissionProp={soumissionAcceptee} />
      <EditeurFacture isOpen={showCreerFinale} onClose={() => setShowCreerFinale(false)} dossier={dossier} client={client} infosEntreprise={infosEntreprise} onSuccess={handleUpdate} typeFacture="finale" factureDepot={factureDepot} ajouts={ajoutsPendantTravaux} soumissionProp={soumissionAcceptee} />
      {/* 🆕 2026-05-15 — Editeur Facture Complémentaire (réutilise 100% le composant existant) */}
      <EditeurFacture
        isOpen={showEditeurComp}
        onClose={() => setShowEditeurComp(false)}
        dossier={dossier}
        client={client}
        infosEntreprise={infosEntreprise}
        onSuccess={handleUpdate}
        typeFacture="complementaire"
        ajouts={[]}
        soumissionProp={null}
        complementaireNumero={facturesComplementaires.length + 1}
      />
      <AjoutModal isOpen={showAjoutModal} onClose={() => setShowAjoutModal(false)} dossier={dossier} ajoutToEdit={ajoutToEdit} onSuccess={handleUpdate} />
      <DemanderSignatureModal isOpen={showDemanderSignature} onClose={() => setShowDemanderSignature(false)} ajout={ajoutPourSignature} dossier={dossier} client={client} factures={factures} onSuccess={handleUpdate} />
    </div>
  );
}
